import { useState, useMemo, useEffect, useRef, useCallback, type DragEvent, type PointerEvent, type UIEvent } from 'react';
import { createPortal } from 'react-dom';
import {
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Clock,
  Zap,
  FileText,
  BarChart3,
  RefreshCcw,
  Eye,
  Trash2,
  Save,
  Send,
  Settings,
  CheckCircle2,
  AlertCircle,
  type LucideIcon,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Button } from '../../components/ui/button';
import { useExclusiveSelect } from '../../hooks/useExclusiveSelect';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  readSavedScheduleLayout,
  readScheduleDraftPosts,
  readSharedScheduleRange,
  readSharedScheduledPosts,
  removeSharedScheduledPost,
  upsertSharedScheduledPost,
  writeSavedScheduleLayout,
  writeScheduleDraftPosts,
  writeSharedScheduleRange,
  type ScheduleDraftPost,
  type SharedScheduledPost,
} from './linkedinScheduleStorage';
import {
  LanguageFlagByCode,
  LANGUAGE_OPTIONS,
  type LanguageCode,
} from '../../components/LanguageFlag';
import { useUiLocale } from '../../../contexts/UiLocaleContext';

type TranslateFn = (key: string, vars?: Record<string, string | number>) => string;

function translatePostStatus(status: PostStatus, t: TranslateFn): string {
  return t(`linkedInSchedule.status.${status.toLowerCase()}`);
}

function translateCampaignStatus(status: CampaignStatus, t: TranslateFn): string {
  return status === 'Active' ? t('campaigns.status.active') : t('common.paused');
}

function translateApprovalStatus(status: string, t: TranslateFn): string {
  if (status === 'Approved') return t('linkedInSchedule.approval.approved');
  if (status === 'Pending') return t('linkedInSchedule.approval.pending');
  if (status === 'Rejected') return t('linkedInSchedule.approval.rejected');
  return status;
}

const VIEW_MODE_I18N = {
  Month: 'linkedInSchedule.viewMonth',
  Week: 'linkedInSchedule.viewWeek',
  List: 'linkedInSchedule.viewList',
} as const;

/* ── Types ── */
type PostStatus = 'Scheduled' | 'Draft' | 'Approved' | 'Posted';
type CampaignStatus = 'Active' | 'Paused';

interface ScheduledPost {
  id: number;
  title: string;
  time: string;
  status: PostStatus;
  language?: string;
}

interface ScheduleFilterOptions {
  campaignFilter: string;
  statusFilter: string;
  languageFilter: string;
  searchQuery: string;
}

interface CampaignRow {
  id: number;
  name: string;
  type: string;
  status: CampaignStatus;
  totalPosts: number;
  completedPosts: number;
  schedule: Record<string, ScheduledPost[]>;
}

interface PostDetail {
  id: number;
  title: string;
  description: string;
  image: string;
  status: PostStatus;
  language: string;
  scheduledDateKey: string;
  scheduledDate: string;
  scheduledTime: string;
  campaignName: string;
  accountName: string;
  objective: string;
  approvalStatus: 'Approved' | 'Pending' | 'Rejected';
  approver: string;
}

interface DraggedPost {
  postId: number;
  sourceCampaignId: number;
  sourceDay: string;
}

type DropPosition = 'before' | 'after' | 'end';

interface DropTarget {
  campaignId: number;
  day: string;
  referencePostId?: number;
  position: DropPosition;
}

interface DraftDropTarget {
  referencePostId?: number;
  position: DropPosition;
}

function getCardDropPosition(event: DragEvent<HTMLElement>): DropPosition {
  const rect = event.currentTarget.getBoundingClientRect();
  return event.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
}

function buildScheduleDropTarget(
  campaignId: number,
  dayKey: string,
  activeDropTarget: DropTarget | null,
  referencePostId?: number,
  event?: DragEvent<HTMLElement>,
): DropTarget {
  if (
    referencePostId != null &&
    activeDropTarget?.campaignId === campaignId &&
    activeDropTarget.day === dayKey &&
    activeDropTarget.referencePostId === referencePostId &&
    (activeDropTarget.position === 'before' || activeDropTarget.position === 'after')
  ) {
    return {
      campaignId,
      day: dayKey,
      referencePostId,
      position: activeDropTarget.position,
    };
  }

  if (referencePostId != null) {
    return {
      campaignId,
      day: dayKey,
      referencePostId,
      position: event ? getCardDropPosition(event) : 'after',
    };
  }

  if (
    activeDropTarget?.campaignId === campaignId &&
    activeDropTarget.day === dayKey &&
    activeDropTarget.position === 'end'
  ) {
    return activeDropTarget;
  }

  return {
    campaignId,
    day: dayKey,
    position: 'end',
  };
}

function computeInsertIndex(posts: ScheduledPost[], target: DropTarget): number {
  if (target.position === 'end' || target.referencePostId == null) {
    return posts.length;
  }

  const refIndex = posts.findIndex((post) => post.id === target.referencePostId);
  if (refIndex === -1) return posts.length;
  return target.position === 'before' ? refIndex : refIndex + 1;
}

function isDropNoOp(posts: ScheduledPost[], dragged: DraggedPost, target: DropTarget): boolean {
  if (dragged.sourceCampaignId !== target.campaignId || dragged.sourceDay !== target.day) {
    return false;
  }

  const fromIndex = posts.findIndex((post) => post.id === dragged.postId);
  if (fromIndex === -1) return false;

  const insertAt = computeInsertIndex(posts, target);
  return insertAt === fromIndex || insertAt === fromIndex + 1;
}

function extractPostById(campaigns: CampaignRow[], postId: number) {
  let movingPost: ScheduledPost | null = null;

  const withoutPost = campaigns.map((campaign) => {
    let updated = false;
    const schedule: CampaignRow['schedule'] = {};

    for (const [day, posts] of Object.entries(campaign.schedule)) {
      const nextPosts = posts.filter((post) => {
        if (post.id !== postId) return true;
        if (!movingPost) movingPost = post;
        updated = true;
        return false;
      });

      if (nextPosts.length > 0) {
        schedule[day] = nextPosts;
      }
    }

    return updated ? { ...campaign, schedule } : campaign;
  });

  return { campaigns: withoutPost, movingPost };
}

function moveScheduledPost(
  campaigns: CampaignRow[],
  dragged: DraggedPost,
  target: DropTarget,
): CampaignRow[] {
  const { campaigns: withoutPost, movingPost } = extractPostById(campaigns, dragged.postId);
  if (!movingPost) return campaigns;

  const withMovedPost = withoutPost.map((campaign) => {
    if (campaign.id !== target.campaignId) return campaign;

    const targetPosts = [...(campaign.schedule[target.day] ?? [])];
    const insertAt = computeInsertIndex(targetPosts, target);
    targetPosts.splice(insertAt, 0, movingPost);

    return {
      ...campaign,
      schedule: {
        ...campaign.schedule,
        [target.day]: targetPosts,
      },
    };
  });

  return withMovedPost.map(syncCampaignRowStats);
}

function updatePostStatusInCampaigns(
  campaigns: CampaignRow[],
  postId: number,
  status: PostStatus,
): CampaignRow[] {
  return campaigns.map((campaign) => {
    let updated = false;
    const schedule: CampaignRow['schedule'] = {};

    for (const [day, posts] of Object.entries(campaign.schedule)) {
      const nextPosts = posts.map((post) => {
        if (post.id !== postId) return post;
        updated = true;
        return { ...post, status };
      });

      if (nextPosts.length > 0) {
        schedule[day] = nextPosts;
      }
    }

    return updated ? syncCampaignRowStats({ ...campaign, schedule }) : campaign;
  });
}

function updatePostMetadataInCampaigns(
  campaigns: CampaignRow[],
  postId: number,
  updates: { title?: string; time?: string; dayKey?: string; language?: string },
): CampaignRow[] {
  return campaigns.map((campaign) => {
    let sourceDay = '';
    let sourcePost: ScheduledPost | null = null;

    for (const [day, posts] of Object.entries(campaign.schedule)) {
      sourcePost = posts.find((post) => post.id === postId) ?? null;
      if (sourcePost) {
        sourceDay = day;
        break;
      }
    }

    if (!sourcePost) return campaign;

    const targetDay = updates.dayKey ?? sourceDay;
    const scheduleWithoutPost = removePostFromSchedule(campaign.schedule, postId);
    const targetPosts = scheduleWithoutPost[targetDay] ?? [];
    const updatedPost: ScheduledPost = {
      ...sourcePost,
      title: updates.title ?? sourcePost.title,
      time: updates.time ?? sourcePost.time,
      language: updates.language ?? sourcePost.language,
    };

    return syncCampaignRowStats({
      ...campaign,
      schedule: {
        ...scheduleWithoutPost,
        [targetDay]: [...targetPosts, updatedPost],
      },
    });
  });
}

function syncSharedPostStatus(campaigns: CampaignRow[], postId: number) {
  for (const campaign of campaigns) {
    for (const [day, posts] of Object.entries(campaign.schedule)) {
      const post = posts.find((item) => item.id === postId);
      if (!post) continue;

      const sharedPost = readSharedScheduledPosts().find((item) => item.id === postId);
      if (!sharedPost) return;

      upsertSharedScheduledPost({
        ...sharedPost,
        campaignId: campaign.id,
        campaignName: campaign.name,
        campaignType: campaign.type,
        title: post.title,
        dateKey: toScheduleDateKey(day),
        time: post.time,
        status: post.status,
        language: resolvePostLanguage(post),
      });
      return;
    }
  }
}

function getCampaignRowStats(campaign: CampaignRow) {
  const scheduledPosts = Object.values(campaign.schedule).flat();
  const scheduledCount = scheduledPosts.length;
  const approvedCount = scheduledPosts.filter(
    (post) => post.status === 'Posted' || post.status === 'Approved',
  ).length;

  return {
    scheduledCount,
    approvedCount,
    totalPosts: scheduledCount,
  };
}

const DEFAULT_POST_LANGUAGE = LANGUAGE_OPTIONS.en.label;

const POST_LANGUAGE_BY_ID: Partial<Record<number, LanguageCode>> = {
  101: 'en', 102: 'de', 103: 'vi', 104: 'fr', 105: 'en',
  201: 'de', 202: 'en', 203: 'vi', 204: 'fr',
  301: 'en', 302: 'de', 303: 'fr', 304: 'vi', 305: 'en',
  401: 'de', 402: 'en', 403: 'vi',
  501: 'fr', 502: 'en', 503: 'de', 504: 'vi',
  601: 'en', 602: 'de', 603: 'fr',
};

function resolvePostLanguage(post: ScheduledPost): string {
  if (post.language) return post.language;
  const code = POST_LANGUAGE_BY_ID[post.id];
  return code ? LANGUAGE_OPTIONS[code].label : DEFAULT_POST_LANGUAGE;
}

function languageCodeFromLabel(label: string): LanguageCode {
  const matched = (Object.keys(LANGUAGE_OPTIONS) as LanguageCode[]).find(
    (code) => LANGUAGE_OPTIONS[code].label === label,
  );
  if (matched) return matched;
  if (label in LANGUAGE_OPTIONS) return label as LanguageCode;
  return 'en';
}

function normalizeScheduledPost(post: ScheduledPost): ScheduledPost {
  return { ...post, language: resolvePostLanguage(post) };
}

function normalizeCampaignLanguage(campaign: CampaignRow): CampaignRow {
  const schedule: CampaignRow['schedule'] = {};

  for (const [day, posts] of Object.entries(campaign.schedule)) {
    schedule[day] = posts.map(normalizeScheduledPost);
  }

  return syncCampaignRowStats({ ...campaign, schedule });
}

function matchesLanguageFilter(postLanguage: string, languageFilter: string): boolean {
  if (languageFilter === 'all') return true;
  const label = LANGUAGE_OPTIONS[languageFilter as LanguageCode]?.label;
  return postLanguage === label;
}

function matchesSearchQuery(
  post: ScheduledPost,
  campaign: CampaignRow,
  query: string,
): boolean {
  if (!query) return true;

  const haystack = [
    post.title,
    campaign.name,
    campaign.type,
    resolvePostLanguage(post),
  ].join(' ').toLowerCase();

  return haystack.includes(query);
}

function matchesCampaignFilters(
  campaign: CampaignRow,
  campaignFilter: string,
  statusFilter: string,
): boolean {
  if (campaignFilter !== 'all' && campaign.id.toString() !== campaignFilter) return false;
  if (statusFilter !== 'all' && campaign.status !== statusFilter) return false;
  return true;
}

function filterCampaignPosts(
  campaign: CampaignRow,
  languageFilter: string,
  query: string,
): CampaignRow['schedule'] {
  const filteredSchedule: CampaignRow['schedule'] = {};

  for (const [day, posts] of Object.entries(campaign.schedule)) {
    const matchedPosts = posts.filter((post) => {
      const postLanguage = resolvePostLanguage(post);
      return matchesLanguageFilter(postLanguage, languageFilter)
        && matchesSearchQuery(post, campaign, query);
    });

    if (matchedPosts.length > 0) {
      filteredSchedule[day] = matchedPosts;
    }
  }

  return filteredSchedule;
}

function applyScheduleFilters(
  campaigns: CampaignRow[],
  options: ScheduleFilterOptions,
): CampaignRow[] {
  const query = options.searchQuery.toLowerCase().trim();
  const hasPostFilters = query.length > 0 || options.languageFilter !== 'all';

  return campaigns.reduce<CampaignRow[]>((result, campaign) => {
    if (!matchesCampaignFilters(campaign, options.campaignFilter, options.statusFilter)) {
      return result;
    }

    if (!hasPostFilters) {
      result.push(campaign);
      return result;
    }

    const filteredSchedule = filterCampaignPosts(campaign, options.languageFilter, query);
    if (Object.keys(filteredSchedule).length === 0) return result;

    result.push({ ...campaign, schedule: filteredSchedule });
    return result;
  }, []);
}

function getProgressBarColor(percent: number): string {
  if (percent < 31) return 'bg-blue-500';
  if (percent < 51) return 'bg-green-500';
  if (percent < 66) return 'bg-yellow-500';
  if (percent < 81) return 'bg-orange-500';
  return 'bg-red-500';
}

function syncCampaignRowStats(campaign: CampaignRow): CampaignRow {
  const { approvedCount, totalPosts } = getCampaignRowStats(campaign);

  return {
    ...campaign,
    totalPosts,
    completedPosts: approvedCount,
  };
}

function toScheduleDateKey(day: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(day)) return day;
  const dayNum = Number(day.split(' ')[1]) || 0;
  return `2025-05-${String(dayNum).padStart(2, '0')}`;
}

function syncSharedPostAfterMove(
  campaigns: CampaignRow[],
  postId: number,
  campaignId: number,
  day: string,
) {
  const campaign = campaigns.find((item) => item.id === campaignId);
  if (!campaign) return;

  const post = (campaign.schedule[day] ?? []).find((item) => item.id === postId);
  if (!post) return;

  const sharedPost = readSharedScheduledPosts().find((item) => item.id === postId);
  if (!sharedPost) return;

  upsertSharedScheduledPost({
    ...sharedPost,
    campaignId: campaign.id,
    campaignName: campaign.name,
    campaignType: campaign.type,
    title: post.title,
    dateKey: toScheduleDateKey(day),
    time: post.time,
    status: post.status,
    language: resolvePostLanguage(post),
  });
}

type ScheduleDay = {
  date: Date;
  key: string;
  weekday: string;
  dayNum: number;
  isToday: boolean;
};

const MOCK_SCHEDULE_ANCHOR = new Date(2025, 4, 18);

function startOfDay(date: Date): Date {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return startOfDay(next);
}

function toDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseDateKey(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return startOfDay(new Date(y, m - 1, d));
}

function formatPickerDate(date: Date, localeTag: string): string {
  return date.toLocaleDateString(localeTag, {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
}

function addMonths(date: Date, months: number): Date {
  return startOfDay(new Date(date.getFullYear(), date.getMonth() + months, 1));
}

function startOfMonth(date: Date): Date {
  return startOfDay(new Date(date.getFullYear(), date.getMonth(), 1));
}

function compareDays(a: Date, b: Date): number {
  return startOfDay(a).getTime() - startOfDay(b).getTime();
}

function clampRangeEnd(start: Date, end: Date): Date {
  const maxEnd = addDays(start, MAX_SCHEDULE_RANGE_DAYS - 1);
  if (compareDays(end, start) < 0) return start;
  if (compareDays(end, maxEnd) > 0) return maxEnd;
  return end;
}

function toTimeInputValue(time: string): string {
  const match = time.match(/^(\d{1,2}):(\d{2})\s?(AM|PM)$/i);
  if (!match) return time;

  const [, rawHour, minute, period] = match;
  let hour = Number(rawHour);
  if (period.toUpperCase() === 'PM' && hour < 12) hour += 12;
  if (period.toUpperCase() === 'AM' && hour === 12) hour = 0;

  return `${String(hour).padStart(2, '0')}:${minute}`;
}

function formatTimeFromInput(value: string): string {
  const [rawHour, minute] = value.split(':');
  const hour = Number(rawHour);
  if (Number.isNaN(hour) || !minute) return value;

  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minute} ${period}`;
}

const NATIVE_DATETIME_INPUT =
  'hawk-native-datetime-input min-w-0 w-full flex-1 border-0 bg-transparent focus:outline-none';

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate()
  );
}

function buildScheduleDays(windowStart: Date, count: number, localeTag: string): ScheduleDay[] {
  const today = startOfDay(new Date());
  return Array.from({ length: count }, (_, index) => {
    const date = addDays(windowStart, index);
    return {
      date,
      key: toDateKey(date),
      weekday: date.toLocaleDateString(localeTag, { weekday: 'short' }),
      dayNum: date.getDate(),
      isToday: isSameDay(date, today),
    };
  });
}

const MAX_SCHEDULE_RANGE_DAYS = 60;
const SCHEDULE_HORIZONTAL_SCROLL_MIN_DAYS = 10;
const WEEKDAY_INDICES = [0, 1, 2, 3, 4, 5, 6] as const;

function getWeekdayLabels(localeTag: string): string[] {
  return WEEKDAY_INDICES.map((dayIndex) =>
    new Date(2024, 0, dayIndex === 0 ? 7 : dayIndex).toLocaleDateString(localeTag, { weekday: 'short' }),
  );
}

function daysBetweenInclusive(start: Date, end: Date): number {
  return Math.max(
    1,
    Math.round((startOfDay(end).getTime() - startOfDay(start).getTime()) / (1000 * 60 * 60 * 24)) + 1,
  );
}

function buildScheduleDaysFromRange(rangeStart: Date, rangeEnd: Date, localeTag: string): ScheduleDay[] {
  const start = startOfDay(rangeStart);
  const end = startOfDay(rangeEnd);
  if (end < start) return buildScheduleDays(start, 1, localeTag);
  const count = Math.min(daysBetweenInclusive(start, end), MAX_SCHEDULE_RANGE_DAYS);
  return buildScheduleDays(start, count, localeTag);
}

function getInitialScheduleRange(): { start: Date; end: Date } {
  const sharedRange = readSharedScheduleRange();
  if (sharedRange) {
    const start = parseDateKey(sharedRange.startKey);
    const end = parseDateKey(sharedRange.endKey);
    return { start, end: clampRangeEnd(start, end) };
  }

  const start = getInitialScheduleWindowStart();
  return { start, end: addDays(start, getViewDayCount('Week') - 1) };
}

function formatScheduleRange(days: ScheduleDay[]): string {
  if (days.length === 0) return '';
  const first = days[0].date;
  const last = days[days.length - 1].date;
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const yearOpts: Intl.DateTimeFormatOptions = { ...opts, year: 'numeric' };
  if (first.getMonth() === last.getMonth() && first.getFullYear() === last.getFullYear()) {
    return `${first.toLocaleDateString('en-US', opts)} – ${last.toLocaleDateString('en-US', yearOpts)}`;
  }
  return `${first.toLocaleDateString('en-US', yearOpts)} – ${last.toLocaleDateString('en-US', yearOpts)}`;
}

function getInitialScheduleWindowStart(): Date {
  return addDays(startOfDay(new Date()), -3);
}

function getViewDayCount(viewMode: 'Month' | 'Week' | 'List'): number {
  if (viewMode === 'Month') return 28;
  if (viewMode === 'List') return 14;
  return 7;
}

function getScheduleStep(viewMode: 'Month' | 'Week' | 'List'): number {
  return viewMode === 'Month' ? 7 : 1;
}

function buildCalendarCells(monthDate: Date): Array<Date | null> {
  const monthStart = startOfMonth(monthDate);
  const firstDayOffset = monthStart.getDay();
  const daysInMonth = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0).getDate();
  const cells: Array<Date | null> = Array.from({ length: firstDayOffset }, () => null);

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(startOfDay(new Date(monthStart.getFullYear(), monthStart.getMonth(), day)));
  }

  while (cells.length % 7 !== 0) {
    cells.push(null);
  }

  return cells;
}

function isDateInRange(date: Date, start: Date, end: Date): boolean {
  return compareDays(date, start) >= 0 && compareDays(date, end) <= 0;
}

function normalizeCampaignSchedule(campaign: CampaignRow): CampaignRow {
  const schedule: CampaignRow['schedule'] = {};

  Object.entries(campaign.schedule).forEach(([day, posts]) => {
    const key = toScheduleDateKey(day);
    schedule[key] = [...(schedule[key] ?? []), ...posts];
  });

  return syncCampaignRowStats({ ...campaign, schedule });
}

function shiftCampaignSchedulesToCurrentWeek(campaigns: CampaignRow[]): CampaignRow[] {
  const offsetDays = Math.round(
    (getInitialScheduleWindowStart().getTime() - startOfDay(MOCK_SCHEDULE_ANCHOR).getTime())
      / (1000 * 60 * 60 * 24),
  );

  if (offsetDays === 0) {
    return campaigns.map(normalizeCampaignSchedule);
  }

  return campaigns.map((campaign) => {
    const schedule: CampaignRow['schedule'] = {};

    Object.entries(campaign.schedule).forEach(([day, posts]) => {
      const key = toScheduleDateKey(day);
      const shiftedKey = toDateKey(addDays(parseDateKey(key), offsetDays));
      schedule[shiftedKey] = [...(schedule[shiftedKey] ?? []), ...posts];
    });

    return syncCampaignRowStats({ ...campaign, schedule });
  });
}

/* ── Mock Data ── */
const LINKEDIN_LOGO = '/Images/logolinkedin.png';

const GLASS_FRAME =
  'border border-white/50 bg-white/45 shadow-[0_8px_32px_rgba(15,23,42,0.08)] backdrop-blur-xl backdrop-saturate-150 rounded-2xl';
const GLASS_CARD =
  'border border-white/55 bg-white/60 shadow-[0_4px_20px_rgba(15,23,42,0.07)] backdrop-blur-md backdrop-saturate-150';
const GLASS_PANEL =
  'border border-white/40 bg-white/35 backdrop-blur-2xl backdrop-saturate-150';
const GLASS_INPUT =
  'border border-white/50 bg-white/50 backdrop-blur-sm';

const HEADER_ACTION_BUTTON_BASE =
  'gap-1.5 border-0 text-xs font-medium text-white shadow-[0_4px_14px_rgba(15,23,42,0.12)] hover:brightness-105';

const HEADER_ACTION_BUTTON_COLORS = {
  auto: 'bg-violet-600 hover:bg-violet-700',
  campaign: 'bg-blue-600 hover:bg-blue-700',
  post: 'bg-emerald-600 hover:bg-emerald-700',
  draft: 'bg-amber-600 hover:bg-amber-700',
  save: 'bg-sky-600 hover:bg-sky-700',
} as const;

function cloneCampaignRows(campaigns: CampaignRow[]): CampaignRow[] {
  return JSON.parse(JSON.stringify(campaigns)) as CampaignRow[];
}

function removePostsFromCampaignsByIds(campaigns: CampaignRow[], postIds: Set<number>): CampaignRow[] {
  if (postIds.size === 0) return campaigns;

  return campaigns.map((campaign) => {
    const schedule: CampaignRow['schedule'] = {};

    for (const [day, posts] of Object.entries(campaign.schedule)) {
      const nextPosts = posts.filter((post) => !postIds.has(post.id));
      if (nextPosts.length > 0) {
        schedule[day] = nextPosts;
      }
    }

    return syncCampaignRowStats({ ...campaign, schedule });
  });
}

function applyDraftPostExclusions(campaigns: CampaignRow[]): CampaignRow[] {
  const draftIds = new Set(readScheduleDraftPosts().map((post) => post.id));
  return removePostsFromCampaignsByIds(campaigns, draftIds);
}

function findFirstScheduledPostId(campaigns: CampaignRow[]): number | null {
  for (const campaign of campaigns) {
    for (const posts of Object.values(campaign.schedule)) {
      if (posts[0]) return posts[0].id;
    }
  }
  return null;
}

function findDraggedPostById(campaigns: CampaignRow[], postId: number): DraggedPost | null {
  for (const campaign of campaigns) {
    for (const [day, posts] of Object.entries(campaign.schedule)) {
      if (posts.some((post) => post.id === postId)) {
        return {
          postId,
          sourceCampaignId: campaign.id,
          sourceDay: day,
        };
      }
    }
  }
  return null;
}

function postExistsOnSchedule(campaigns: CampaignRow[], postId: number): boolean {
  return findDraggedPostById(campaigns, postId) != null;
}

function rebuildCampaignsFromStorage(): CampaignRow[] {
  const saved = readSavedScheduleLayout<CampaignRow[]>();
  if (saved && Array.isArray(saved) && saved.length > 0) {
    return applyDraftPostExclusions(
      mergeSharedScheduledPosts(saved, readSharedScheduledPosts()).map(normalizeCampaignLanguage),
    );
  }

  return buildInitialCampaigns();
}

function getCommittedScheduleLayout(): CampaignRow[] {
  return rebuildCampaignsFromStorage();
}

const CAMPAIGN_ACCOUNTS: Record<number, string> = {
  1: 'Rachel Taylor',
  2: 'James Morrison',
  3: 'Sarah Chen',
  4: 'Michael Brooks',
  5: 'Emily Watson',
  6: 'David Nguyen',
};

const STATUS_HOVER_BADGE: Record<PostStatus, string> = {
  Draft: 'bg-amber-100 text-amber-700',
  Scheduled: 'bg-amber-100 text-amber-700',
  Approved: 'bg-green-100 text-green-700',
  Posted: 'bg-slate-100 text-slate-600',
};

function formatScheduleDayDate(dayKey: string, localeTag: string): string {
  return parseDateKey(dayKey).toLocaleDateString(localeTag, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDayKeyLabel(dayKey: string, localeTag: string): string {
  const date = parseDateKey(dayKey);
  return `${date.toLocaleDateString(localeTag, { weekday: 'short' })} ${date.getDate()}`;
}

function hasScheduleConflict(
  campaigns: CampaignRow[],
  day: string,
  post: ScheduledPost,
): boolean {
  return campaigns.some((campaign) =>
    (campaign.schedule[day] ?? []).some(
      (other) => other.id !== post.id && other.time === post.time,
    ),
  );
}

interface SchedulePostHoverCardProps {
  accountName: string;
  post: ScheduledPost;
  campaignName: string;
  campaignType: string;
  day: string;
  hasConflict: boolean;
}

interface DayPostRow {
  post: ScheduledPost;
  campaignId: number;
  campaignName: string;
  campaignType: string;
  accountName: string;
  hasConflict: boolean;
}

interface DayPostsDetailPanelProps {
  day: string;
  rows: DayPostRow[];
  onSelectPost: (postId: number) => void;
}

function SchedulePostDetailCard({
  accountName,
  post,
  campaignName,
  campaignType,
  day,
  hasConflict,
  onClick,
}: SchedulePostHoverCardProps & { onClick?: () => void }) {
  const { t, localeTag } = useUiLocale();
  const rows = [
    { label: t('linkedInSchedule.hoverLocation'), value: campaignName },
    { label: t('linkedInSchedule.hoverRole'), value: campaignType },
    { label: t('linkedInSchedule.hoverTime'), value: post.time },
    { label: t('linkedInSchedule.hoverDate'), value: formatScheduleDayDate(day, localeTag) },
  ];

  const content = (
    <>
      <div className="mb-3 flex items-start justify-between gap-2">
        <p className="text-sm font-semibold text-blue-600">{accountName}</p>
        <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${STATUS_HOVER_BADGE[post.status]}`}>
          {translatePostStatus(post.status, t)}
        </span>
      </div>

      <dl className="space-y-1.5">
        {rows.map(({ label, value }) => (
          <div key={label} className="flex items-center justify-between gap-3 text-xs">
            <dt className="text-slate-400">{label}</dt>
            <dd className="truncate text-right font-medium text-slate-800">{value}</dd>
          </div>
        ))}
      </dl>

      {hasConflict && (
        <div className="mt-3 flex items-center gap-1.5 rounded-lg bg-red-50 px-2.5 py-2 text-[11px] font-medium text-red-600">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          {t('linkedInSchedule.scheduleConflict')}
        </div>
      )}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`w-full rounded-xl p-3 text-left transition-all hover:border-blue-300/60 hover:shadow-[0_8px_28px_rgba(59,130,246,0.12)] ${GLASS_CARD}`}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={`rounded-xl p-3 shadow-[0_12px_40px_rgba(15,23,42,0.12)] ${GLASS_CARD}`}>
      {content}
    </div>
  );
}

function draftPostToScheduledPost(draftPost: ScheduleDraftPost): ScheduledPost {
  return {
    id: draftPost.id,
    title: draftPost.title,
    time: draftPost.time,
    status: draftPost.status,
    language: draftPost.language,
  };
}

function insertDraftPostIntoSchedule(
  campaigns: CampaignRow[],
  scheduledPost: ScheduledPost,
  target: DropTarget,
): CampaignRow[] {
  const withoutExistingPost = removePostsFromCampaignsByIds(campaigns, new Set([scheduledPost.id]));

  return withoutExistingPost
    .map((campaign) => {
      if (campaign.id !== target.campaignId) return campaign;

      const targetPosts = [...(campaign.schedule[target.day] ?? [])];
      const insertAt = computeInsertIndex(targetPosts, target);
      targetPosts.splice(insertAt, 0, scheduledPost);

      return {
        ...campaign,
        schedule: {
          ...campaign.schedule,
          [target.day]: targetPosts,
        },
      };
    })
    .map(syncCampaignRowStats);
}

function restoreDraftPostToSharedStorage(
  draftPost: ScheduleDraftPost,
  campaign: CampaignRow,
  dayKey: string,
  scheduledPostId: number,
) {
  const existing = readSharedScheduledPosts().find((item) => item.id === draftPost.id);

  upsertSharedScheduledPost({
    id: scheduledPostId,
    campaignId: campaign.id,
    campaignName: campaign.name,
    campaignType: campaign.type,
    title: draftPost.title,
    description: existing?.description ?? `Post "${draftPost.title}" scheduled.`,
    dateKey: toScheduleDateKey(dayKey),
    time: draftPost.time,
    status: draftPost.status,
    language: draftPost.language,
  });
}

function movePostFromScheduleToDraft(
  campaigns: CampaignRow[],
  dragged: DraggedPost,
): { draftPost: ScheduleDraftPost; nextCampaigns: CampaignRow[] } | null {
  const sourceCampaign = campaigns.find((campaign) => campaign.id === dragged.sourceCampaignId);
  if (!sourceCampaign) return null;

  const { campaigns: withoutPost, movingPost: extractedPost } = extractPostById(campaigns, dragged.postId);
  if (!extractedPost) return null;

  const movingPost: ScheduledPost = extractedPost;

  return {
    draftPost: {
      id: movingPost.id,
      title: movingPost.title,
      time: movingPost.time,
      status: movingPost.status,
      language: resolvePostLanguage(movingPost),
      campaignId: sourceCampaign.id,
      campaignName: sourceCampaign.name,
      campaignType: sourceCampaign.type,
      sourceDayKey: dragged.sourceDay,
      savedAt: new Date().toISOString(),
    },
    nextCampaigns: withoutPost.map(syncCampaignRowStats),
  };
}

function computeDraftInsertIndex(posts: ScheduleDraftPost[], target: DraftDropTarget): number {
  if (target.position === 'end' || target.referencePostId == null) {
    return posts.length;
  }

  const refIndex = posts.findIndex((post) => post.id === target.referencePostId);
  if (refIndex === -1) return posts.length;
  return target.position === 'before' ? refIndex : refIndex + 1;
}

function insertDraftPostAtTarget(
  posts: ScheduleDraftPost[],
  draftPost: ScheduleDraftPost,
  target: DraftDropTarget,
): ScheduleDraftPost[] {
  if (target.referencePostId === draftPost.id) return posts;

  const nextPosts = posts.filter((post) => post.id !== draftPost.id);
  const insertAt = computeDraftInsertIndex(nextPosts, target);
  nextPosts.splice(insertAt, 0, draftPost);
  return nextPosts;
}

interface ScheduleDraftPanelProps {
  posts: ScheduleDraftPost[];
  draggedDraftPostId: number | null;
  dropTarget: DraftDropTarget | null;
  onDragOver: (event: DragEvent<HTMLElement>, target: DraftDropTarget) => void;
  onDragLeave: (event: DragEvent<HTMLDivElement>) => void;
  onDrop: (event: DragEvent<HTMLElement>, target: DraftDropTarget) => void;
  onDragDraftStart: (postId: number) => void;
  onDragDraftEnd: () => void;
  onRemove: (postId: number) => void;
}

function ScheduleDraftPanel({
  posts,
  draggedDraftPostId,
  dropTarget,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragDraftStart,
  onDragDraftEnd,
  onRemove,
}: ScheduleDraftPanelProps) {
  const { t, localeTag } = useUiLocale();
  return (
    <div
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
      onDragOver={(event) => onDragOver(event, { position: 'end' })}
      onDragLeave={onDragLeave}
      onDrop={(event) => onDrop(event, { position: 'end' })}
    >
      <div className="mt-4 flex items-center justify-between px-4">
        <p className="text-xs font-semibold text-slate-700">{t('linkedInSchedule.storedPosts')}</p>
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
          {posts.length}
        </span>
      </div>

      <div className="mt-2 flex-1 overflow-y-auto px-4 pb-4 scrollbar-hide">
        {posts.length === 0 ? (
          <div className="flex min-h-[7.5rem] items-center justify-center px-4 text-center text-sm text-slate-400">
            {t('linkedInSchedule.draftEmpty')}
          </div>
        ) : (
          <ul className="space-y-2">
            {posts.map((post) => {
              const style = POST_CARD_STYLE[post.status];
              const isDragging = draggedDraftPostId === post.id;
              const isDropBefore =
                dropTarget?.referencePostId === post.id && dropTarget.position === 'before';
              const isDropAfter =
                dropTarget?.referencePostId === post.id && dropTarget.position === 'after';
              return (
                <li
                  key={post.id}
                  draggable
                  onDragStart={() => onDragDraftStart(post.id)}
                  onDragEnd={onDragDraftEnd}
                  onDragOver={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    if (draggedDraftPostId === post.id) return;
                    event.dataTransfer.dropEffect = 'move';
                    onDragOver(event, {
                      referencePostId: post.id,
                      position: getCardDropPosition(event),
                    });
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    if (draggedDraftPostId === post.id) return;
                    onDrop(event, {
                      referencePostId: post.id,
                      position: getCardDropPosition(event),
                    });
                  }}
                  className={`relative cursor-grab rounded-xl border p-3 active:cursor-grabbing ${style.bg} ${style.border} ${
                    isDragging ? 'opacity-40' : ''
                  }`}
                >
                  {isDropBefore && (
                    <div className="pointer-events-none absolute -top-1 left-0 right-0 z-10 h-0.5 rounded-full bg-amber-500 shadow-[0_0_0_1px_rgba(245,158,11,0.35)]" />
                  )}
                  <div className="flex items-start gap-2">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-blue-600">
                      <LinkedInLogo className="h-4 w-4 object-contain" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`line-clamp-2 text-sm font-semibold leading-snug ${style.label}`}>
                        {post.title}
                      </p>
                      <p className="mt-0.5 truncate text-[11px] text-slate-500">{post.campaignName}</p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[10px] text-slate-500">
                        <span className={`rounded-full px-2 py-0.5 font-medium ${STATUS_HOVER_BADGE[post.status]}`}>
                          {translatePostStatus(post.status, t)}
                        </span>
                        <span>{post.time}</span>
                        <span className="truncate">{formatScheduleDayDate(post.sourceDayKey, localeTag)}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => onRemove(post.id)}
                      onMouseDown={(event) => event.stopPropagation()}
                      className="shrink-0 rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500"
                      aria-label={t('linkedInSchedule.removeFromDraft', { title: post.title })}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {isDropAfter && (
                    <div className="pointer-events-none absolute -bottom-1 left-0 right-0 z-10 h-0.5 rounded-full bg-amber-500 shadow-[0_0_0_1px_rgba(245,158,11,0.35)]" />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function DayPostsDetailPanel({ day, rows, onSelectPost }: DayPostsDetailPanelProps) {
  const { t, localeTag } = useUiLocale();
  return (
    <div className="min-h-0 flex-1 overflow-y-auto scrollbar-hide">
      <div className="space-y-3 px-4 py-4">
        <div>
          <p className="text-xs text-slate-500">{formatScheduleDayDate(day, localeTag)}</p>
          <p className="mt-0.5 text-sm font-semibold text-slate-900">
            {t('linkedInSchedule.dayPostsCount', {
              count: rows.length,
              day: formatDayKeyLabel(day, localeTag),
            })}
          </p>
        </div>

        {rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">{t('linkedInSchedule.noPostsDay')}</p>
        ) : (
          rows.map(({ post, campaignName, campaignType, accountName, hasConflict }) => (
            <SchedulePostDetailCard
              key={post.id}
              accountName={accountName}
              post={post}
              campaignName={campaignName}
              campaignType={campaignType}
              day={day}
              hasConflict={hasConflict}
              onClick={() => onSelectPost(post.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

type SchedulePostTooltipState = SchedulePostHoverCardProps & {
  anchorRect: DOMRect;
};

function SchedulePostHoverCardPortal({
  anchorRect,
  ...cardProps
}: SchedulePostTooltipState) {
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      role="tooltip"
      className="pointer-events-none fixed z-[9999] w-[260px] -translate-x-1/2 -translate-y-full transition-[left,top] duration-200 ease-out"
      style={{
        left: anchorRect.left + anchorRect.width / 2,
        top: anchorRect.top - 8,
      }}
    >
      <SchedulePostDetailCard {...cardProps} />
    </div>,
    document.body,
  );
}

function isNodeInsideScheduleGrid(
  node: EventTarget | null,
  gridRoot: HTMLDivElement | null,
): boolean {
  return node instanceof Node && gridRoot != null && gridRoot.contains(node);
}

function LinkedInLogo({ className = 'h-4 w-4 shrink-0 object-contain' }: { className?: string }) {
  return <img src={LINKEDIN_LOGO} alt="LinkedIn" className={className} />;
}

const MOCK_CAMPAIGNS: CampaignRow[] = [
  {
    id: 1, name: 'Brand Awareness Q2', type: 'Awareness Campaign', status: 'Active', totalPosts: 10, completedPosts: 6,
    schedule: {
      'Mon 19': [
        { id: 101, title: 'The Future of B2B...', time: '10:00 AM', status: 'Approved' },
        { id: 102, title: 'Building Stronger...', time: '10:00 AM', status: 'Approved' },
      ],
      'Wed 21': [{ id: 103, title: 'Why Brand Trust...', time: '10:00 AM', status: 'Approved' }],
      'Thu 22': [{ id: 104, title: 'Data that Drives...', time: '10:00 AM', status: 'Scheduled' }],
      'Fri 23': [{ id: 105, title: 'Q2 Brand Update', time: '10:00 AM', status: 'Scheduled' }],
    },
  },
  {
    id: 2, name: 'Product Launch 2025', type: 'Product Launch', status: 'Active', totalPosts: 8, completedPosts: 5,
    schedule: {
      'Mon 19': [{ id: 201, title: 'Introducing Our...', time: '11:00 AM', status: 'Scheduled' }],
      'Tue 20': [{ id: 202, title: 'New Feature, More...', time: '11:00 AM', status: 'Scheduled' }],
      'Wed 21': [{ id: 203, title: 'How It Works', time: '11:00 AM', status: 'Approved' }],
      'Thu 22': [{ id: 204, title: 'Customer Benefits', time: '11:00 AM', status: 'Draft' }],
    },
  },
  {
    id: 3, name: 'Thought Leadership Series', type: 'Content Campaign', status: 'Active', totalPosts: 8, completedPosts: 4,
    schedule: {
      'Mon 19': [{ id: 301, title: 'Leadership in the...', time: '9:00 AM', status: 'Approved' }],
      'Tue 20': [{ id: 302, title: '5 Lessons from...', time: '9:00 AM', status: 'Approved' }],
      'Wed 21': [{ id: 303, title: 'The Future of Work', time: '9:00 AM', status: 'Approved' }],
      'Thu 22': [{ id: 304, title: 'Expert Q&A', time: '9:00 AM', status: 'Draft' }],
      'Fri 23': [{ id: 305, title: 'Industry Trends', time: '9:00 AM', status: 'Scheduled' }],
    },
  },
  {
    id: 4, name: 'Events Promotion', type: 'Event Campaign', status: 'Paused', totalPosts: 6, completedPosts: 2,
    schedule: {
      'Tue 20': [{ id: 401, title: 'Webinar: AI in B2B', time: '12:00 PM', status: 'Approved' }],
      'Thu 22': [{ id: 402, title: 'Register Now', time: '12:00 PM', status: 'Scheduled' }],
      'Fri 23': [{ id: 403, title: 'Event Recap', time: '12:00 PM', status: 'Draft' }],
    },
  },
  {
    id: 5, name: 'Customer Success Stories', type: 'Engagement Campaign', status: 'Active', totalPosts: 6, completedPosts: 3,
    schedule: {
      'Mon 19': [{ id: 501, title: 'Customer Spotlight', time: '2:00 PM', status: 'Approved' }],
      'Tue 20': [{ id: 502, title: 'How Acme Scaled...', time: '2:00 PM', status: 'Approved' }],
      'Wed 21': [{ id: 503, title: 'Results that Matter', time: '2:00 PM', status: 'Draft' }],
      'Fri 23': [{ id: 504, title: 'Client Testimonial', time: '2:00 PM', status: 'Approved' }],
    },
  },
  {
    id: 6, name: 'Industry Insights', type: 'Thought Leadership', status: 'Active', totalPosts: 6, completedPosts: 2,
    schedule: {
      'Tue 20': [{ id: 601, title: 'Weekly Insight', time: '4:00 PM', status: 'Approved' }],
      'Thu 22': [{ id: 602, title: 'Market Update', time: '4:00 PM', status: 'Scheduled' }],
      'Fri 23': [{ id: 603, title: 'What to Watch', time: '4:00 PM', status: 'Draft' }],
    },
  },
];

const DEFAULT_POST_DETAIL: PostDetail = {
  id: 202,
  title: 'New Feature, More Impact',
  description: 'Introducing our new feature that helps you streamline campaign performance tracking and insights.',
  image: '',
  status: 'Scheduled',
  language: 'English (EN)',
  scheduledDateKey: '2025-05-20',
  scheduledDate: 'May 20, 2025',
  scheduledTime: '11:00 AM',
  campaignName: 'Product Launch 2025',
  accountName: 'LinkedIn Account',
  objective: 'Drive feature awareness and trial sign-ups',
  approvalStatus: 'Approved',
  approver: 'Sarah Johnson',
};

const STATUS_DOT_COLOR: Record<PostStatus, string> = {
  Draft: 'bg-blue-600', // #2563EB
  Scheduled: 'bg-amber-500', // #F59E0B
  Approved: 'bg-green-500', // #22C55E
  Posted: 'bg-slate-500', // #6B7280
};

function removePostFromSchedule(schedule: CampaignRow['schedule'], postId: number) {
  return Object.fromEntries(
    Object.entries(schedule).map(([day, posts]) => [
      day,
      posts.filter((post) => post.id !== postId),
    ]),
  );
}

function mergeSharedScheduledPosts(baseCampaigns: CampaignRow[], sharedPosts: SharedScheduledPost[]) {
  return sharedPosts.reduce<CampaignRow[]>((campaigns, sharedPost) => {
    const targetDay = sharedPost.dateKey;
    const scheduledPost: ScheduledPost = {
      id: sharedPost.id,
      title: sharedPost.title,
      time: sharedPost.time,
      status: sharedPost.status,
      language: sharedPost.language,
    };

    const campaignIndex = campaigns.findIndex(
      (campaign) => campaign.id === sharedPost.campaignId || campaign.name === sharedPost.campaignName,
    );
    const existingIndex =
      campaignIndex === -1
        ? -1
        : (campaigns[campaignIndex].schedule[targetDay] ?? []).findIndex((post) => post.id === sharedPost.id);

    const campaignsWithoutPost = removePostsFromCampaignsByIds(campaigns, new Set([sharedPost.id]));
    const nextCampaignIndex = campaignsWithoutPost.findIndex(
      (campaign) => campaign.id === sharedPost.campaignId || campaign.name === sharedPost.campaignName,
    );

    if (nextCampaignIndex === -1) {
      return [
        ...campaignsWithoutPost,
        {
          id: sharedPost.campaignId,
          name: sharedPost.campaignName,
          type: sharedPost.campaignType,
          status: 'Active',
          totalPosts: 1,
          completedPosts: sharedPost.status === 'Posted' ? 1 : 0,
          schedule: {
            [targetDay]: [scheduledPost],
          },
        },
      ];
    }

    return campaignsWithoutPost.map((campaign, index) => {
      if (index !== nextCampaignIndex) return campaign;

      const targetPosts = [...(campaign.schedule[targetDay] ?? [])];
      if (existingIndex !== -1) {
        const insertAt = Math.min(existingIndex, targetPosts.length);
        targetPosts.splice(insertAt, 0, scheduledPost);
      } else {
        targetPosts.push(scheduledPost);
      }

      return {
        ...campaign,
        schedule: {
          ...campaign.schedule,
          [targetDay]: targetPosts,
        },
      };
    });
  }, baseCampaigns).map(syncCampaignRowStats);
}

function buildInitialCampaigns(): CampaignRow[] {
  return applyDraftPostExclusions(
    mergeSharedScheduledPosts(
      shiftCampaignSchedulesToCurrentWeek(MOCK_CAMPAIGNS),
      readSharedScheduledPosts(),
    ).map(normalizeCampaignLanguage),
  );
}

const STATUS_TEXT_COLOR: Record<PostStatus, string> = {
  Draft: 'text-blue-700', // #1D4ED8
  Scheduled: 'text-amber-700', // #B45309
  Approved: 'text-green-700', // #15803D
  Posted: 'text-slate-600', // #4B5563
};

const POST_CARD_STYLE: Record<PostStatus, { bg: string; border: string; icon: string; label: string }> = {
  Draft: {
    bg: 'bg-blue-400/15 backdrop-blur-md',
    border: 'border-blue-300/45',
    icon: 'text-blue-600',
    label: STATUS_TEXT_COLOR.Draft,
  },
  Scheduled: {
    bg: 'bg-amber-400/15 backdrop-blur-md',
    border: 'border-amber-300/45',
    icon: 'text-amber-600',
    label: STATUS_TEXT_COLOR.Scheduled,
  },
  Approved: {
    bg: 'bg-green-400/15 backdrop-blur-md',
    border: 'border-green-300/45',
    icon: 'text-green-600',
    label: STATUS_TEXT_COLOR.Approved,
  },
  Posted: {
    bg: 'bg-slate-400/10 backdrop-blur-md',
    border: 'border-slate-300/45',
    icon: 'text-slate-500',
    label: STATUS_TEXT_COLOR.Posted,
  },
};

interface StatCardProps {
  icon: LucideIcon;
  iconBg: string;
  iconColor: string;
  label: string;
  value: string | number;
  sub: string;
  subColor?: string;
}

function StatCard({ icon: Icon, iconBg, iconColor, label, value, sub, subColor = 'text-slate-500' }: StatCardProps) {
  return (
    <div className={`flex items-center gap-3 rounded-2xl p-4 ${GLASS_CARD}`}>
      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl backdrop-blur-sm ${iconBg}`}>
        <Icon className={`h-5 w-5 ${iconColor}`} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-xl font-bold text-slate-900">{value}</p>
        <p className={`truncate text-[11px] ${subColor}`}>{sub}</p>
      </div>
    </div>
  );
}

interface DateRangePickerProps {
  start: Date;
  end: Date;
  onRangeChange: (start: Date, end: Date) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  exclusiveSelectId?: string;
  exclusiveSelectTriggerClass?: string;
  onTriggerPointerDown?: (event: PointerEvent<HTMLButtonElement>) => void;
}

interface RangeCalendarMonthProps {
  monthDate: Date;
  draftStart: Date;
  draftEnd: Date;
  selectingEnd: boolean;
  onSelectDate: (date: Date) => void;
}

function rangeCellClass(date: Date, draftStart: Date, draftEnd: Date): string {
  const isStart = isSameDay(date, draftStart);
  const isEnd = isSameDay(date, draftEnd);
  const isSelectedEdge = isStart || isEnd;
  const isInRange = isDateInRange(date, draftStart, draftEnd);

  if (isSelectedEdge) {
    return 'bg-blue-600 text-white shadow-[0_4px_12px_-5px_rgba(37,99,235,0.8)]';
  }

  if (isInRange) {
    return 'bg-blue-100/90 text-blue-900';
  }

  return 'text-slate-700 hover:bg-blue-50 hover:text-blue-700';
}

function getDateRangePanelStyle(anchorRect: DOMRect): React.CSSProperties {
  const pageMargin = 16;
  const width = Math.min(576, window.innerWidth - pageMargin * 2);
  const left = Math.min(
    Math.max(pageMargin, anchorRect.right - width),
    window.innerWidth - width - pageMargin,
  );

  return {
    left,
    top: anchorRect.bottom + 8,
    width,
    maxHeight: `calc(100vh - ${anchorRect.bottom + pageMargin}px)`,
  };
}

function RangeCalendarMonth({
  monthDate,
  draftStart,
  draftEnd,
  selectingEnd,
  onSelectDate,
}: RangeCalendarMonthProps) {
  const { localeTag } = useUiLocale();
  const cells = buildCalendarCells(monthDate);
  const monthLabel = monthDate.toLocaleDateString(localeTag, { month: 'long', year: 'numeric' });
  const maxEndDate = addDays(draftStart, MAX_SCHEDULE_RANGE_DAYS - 1);
  const weekdayLabels = getWeekdayLabels(localeTag);

  return (
    <div className="min-w-[16rem] flex-1">
      <div className="mb-2 flex items-center justify-center">
        <p className="text-xs font-semibold text-slate-700">{monthLabel}</p>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {weekdayLabels.map((day) => (
          <span key={day} className="text-[10px] font-medium text-slate-400">
            {day}
          </span>
        ))}
        {cells.map((date, index) => {
          if (!date) return <span key={`empty-${index}`} className="h-7" />;

          const disabled = selectingEnd && compareDays(date, maxEndDate) > 0;
          return (
            <button
              key={toDateKey(date)}
              type="button"
              disabled={disabled}
              onClick={() => onSelectDate(date)}
              className={`h-7 rounded-md text-xs font-semibold transition-colors disabled:pointer-events-none disabled:text-slate-300 ${
                disabled ? '' : rangeCellClass(date, draftStart, draftEnd)
              }`}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DateRangePicker({
  start,
  end,
  onRangeChange,
  open,
  onOpenChange,
  exclusiveSelectId,
  exclusiveSelectTriggerClass,
  onTriggerPointerDown,
}: DateRangePickerProps) {
  const { t, localeTag } = useUiLocale();
  const skipNextClickRef = useRef(false);
  const [draftStart, setDraftStart] = useState(start);
  const [draftEnd, setDraftEnd] = useState(end);
  const [visibleMonth, setVisibleMonth] = useState(() => startOfMonth(start));
  const [selectingEnd, setSelectingEnd] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  const updateAnchorRect = () => {
    const nextRect = triggerRef.current?.getBoundingClientRect();
    if (nextRect) setAnchorRect(nextRect);
  };

  useEffect(() => {
    if (!open) return undefined;

    const closeOnOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      onOpenChange(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onOpenChange(false);
    };

    updateAnchorRect();
    window.addEventListener('mousedown', closeOnOutside);
    window.addEventListener('keydown', closeOnEscape);
    window.addEventListener('resize', updateAnchorRect);
    window.addEventListener('scroll', updateAnchorRect, true);
    return () => {
      window.removeEventListener('mousedown', closeOnOutside);
      window.removeEventListener('keydown', closeOnEscape);
      window.removeEventListener('resize', updateAnchorRect);
      window.removeEventListener('scroll', updateAnchorRect, true);
    };
  }, [open, onOpenChange]);

  useEffect(() => {
    if (open) {
      setDraftStart(start);
      setDraftEnd(end);
      setVisibleMonth(startOfMonth(start));
      setSelectingEnd(false);
    }
  }, [end, open, start]);

  const handleSelectDate = (date: Date) => {
    if (!selectingEnd) {
      setDraftStart(date);
      setDraftEnd(date);
      setSelectingEnd(true);
      return;
    }

    if (compareDays(date, draftStart) < 0) {
      setDraftStart(date);
      setDraftEnd(draftStart);
    } else {
      setDraftEnd(clampRangeEnd(draftStart, date));
    }
    setSelectingEnd(false);
  };

  const applyDraftRange = () => {
    onRangeChange(draftStart, clampRangeEnd(draftStart, draftEnd));
    onOpenChange(false);
  };

  const clearDraftRange = () => {
    const initial = getInitialScheduleRange();
    setDraftStart(initial.start);
    setDraftEnd(initial.end);
    setVisibleMonth(startOfMonth(initial.start));
    setSelectingEnd(false);
  };

  const handleTriggerPointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    if (!onTriggerPointerDown) return;
    onTriggerPointerDown(event);
    if (event.defaultPrevented) {
      skipNextClickRef.current = true;
    }
  };

  const handleTriggerClick = () => {
    if (skipNextClickRef.current) {
      skipNextClickRef.current = false;
      updateAnchorRect();
      return;
    }
    updateAnchorRect();
    onOpenChange(!open);
  };

  const pickerPanel = open && anchorRect && typeof document !== 'undefined'
    ? createPortal(
      <div
        ref={panelRef}
        className="fixed z-[9999] overflow-y-auto rounded-2xl border border-white/70 bg-white/95 p-3 shadow-[0_24px_70px_-28px_rgba(15,23,42,0.5)] ring-1 ring-blue-100 backdrop-blur-xl"
        style={getDateRangePanelStyle(anchorRect)}
      >
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-900">{t('linkedInSchedule.datePickerTitle')}</p>
            <p className="text-[11px] text-slate-500">
              {selectingEnd ? t('linkedInSchedule.chooseEndDate') : t('linkedInSchedule.chooseStartDate')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"
            aria-label={t('linkedInSchedule.closeDatePicker')}
          >
            ×
          </button>
        </div>

        <div className="mb-3 grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
            <p className="text-[11px] font-medium text-slate-500">{t('linkedInSchedule.startDate')}</p>
            <p className="text-xs font-semibold text-slate-800">{formatPickerDate(draftStart, localeTag)}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
            <p className="text-[11px] font-medium text-slate-500">{t('linkedInSchedule.endDate')}</p>
            <p className="text-xs font-semibold text-slate-800">{formatPickerDate(draftEnd, localeTag)}</p>
          </div>
        </div>

        <div className="mb-3 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setVisibleMonth((month) => addMonths(month, -1))}
            className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100"
            aria-label={t('linkedInSchedule.prevMonth')}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setVisibleMonth((month) => addMonths(month, 1))}
            className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100"
            aria-label={t('linkedInSchedule.nextMonth')}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <div className="flex gap-5 border-y border-slate-100 py-3">
          <RangeCalendarMonth
            monthDate={visibleMonth}
            draftStart={draftStart}
            draftEnd={draftEnd}
            selectingEnd={selectingEnd}
            onSelectDate={handleSelectDate}
          />
          <RangeCalendarMonth
            monthDate={addMonths(visibleMonth, 1)}
            draftStart={draftStart}
            draftEnd={draftEnd}
            selectingEnd={selectingEnd}
            onSelectDate={handleSelectDate}
          />
        </div>

        <div className="mt-3 flex items-center justify-end gap-2">
          <Button type="button" variant="outline" size="sm" className="h-8 min-w-[6.5rem] text-xs" onClick={clearDraftRange}>
            {t('linkedInSchedule.clearDates')}
          </Button>
          <Button type="button" size="sm" className="h-8 min-w-[7.5rem] bg-blue-600 text-xs text-white hover:bg-blue-700" onClick={applyDraftRange}>
            {t('linkedInSchedule.saveDates')}
          </Button>
        </div>
      </div>,
      document.body,
    )
    : null;

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        data-exclusive-select-id={exclusiveSelectId}
        onPointerDown={onTriggerPointerDown ? handleTriggerPointerDown : undefined}
        onClick={handleTriggerClick}
        className={`${exclusiveSelectTriggerClass ?? ''} flex h-9 min-w-[13rem] items-center gap-2 rounded-lg px-3 text-xs text-slate-700 transition-colors hover:bg-white/70 focus:border-blue-300/60 focus:outline-none focus:ring-1 focus:ring-blue-200/80 ${GLASS_INPUT}`}
        aria-expanded={open}
      >
        <Calendar className="h-3.5 w-3.5 shrink-0 text-slate-400" />
        <span className="truncate font-medium tabular-nums">
          {formatPickerDate(start, localeTag)} - {formatPickerDate(end, localeTag)}
        </span>
      </button>

      {pickerPanel}
    </div>
  );
}

export function LinkedInScheduledPage() {
  const { t, localeTag } = useUiLocale();
  const [campaigns, setCampaigns] = useState<CampaignRow[]>(getCommittedScheduleLayout);
  const savedScheduleRef = useRef<CampaignRow[]>(cloneCampaignRows(getCommittedScheduleLayout()));
  const [campaignFilter, setCampaignFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [languageFilter, setLanguageFilter] = useState('all');
  const {
    openId: openToolbarDropdown,
    setOpenId: setOpenToolbarDropdown,
    onOpenChange: onToolbarOpenChange,
    onSelectContentOutside: onToolbarSelectContentOutside,
    onTriggerPointerDown: onToolbarTriggerPointerDown,
    exclusiveSelectGroupId: toolbarSelectGroupId,
    exclusiveSelectTriggerClass: toolbarSelectTriggerClass,
  } = useExclusiveSelect<'campaign' | 'status' | 'language' | 'date'>('linkedin-schedule-toolbar');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPostId, setSelectedPostId] = useState<number | null>(() => {
    const initialCampaigns = getCommittedScheduleLayout();
    return findFirstScheduledPostId(initialCampaigns) ?? 202;
  });
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'Month' | 'Week' | 'List'>('Week');
  const [rangeStart, setRangeStart] = useState(() => getInitialScheduleRange().start);
  const [rangeEnd, setRangeEnd] = useState(() => getInitialScheduleRange().end);
  const [draggedPost, setDraggedPost] = useState<DraggedPost | null>(null);
  const [draggedDraftPostId, setDraggedDraftPostId] = useState<number | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);
  const [hoverTooltip, setHoverTooltip] = useState<SchedulePostTooltipState | null>(null);
  const [isDraftPanelOpen, setIsDraftPanelOpen] = useState(false);
  const [draftPosts, setDraftPosts] = useState<ScheduleDraftPost[]>(readScheduleDraftPosts);
  const [draftDropTarget, setDraftDropTarget] = useState<DraftDropTarget | null>(null);
  const scheduleHeaderRef = useRef<HTMLDivElement | null>(null);
  const scheduleBodyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const syncSharedPosts = () => {
      const nextCampaigns = rebuildCampaignsFromStorage();
      const layoutSnapshot = cloneCampaignRows(nextCampaigns);
      setCampaigns(nextCampaigns);
      savedScheduleRef.current = layoutSnapshot;
      setSelectedPostId((currentId) => {
        if (currentId != null && postExistsOnSchedule(nextCampaigns, currentId)) return currentId;
        return findFirstScheduledPostId(nextCampaigns);
      });
    };

    window.addEventListener('storage', syncSharedPosts);
    window.addEventListener('linkedin-schedule-updated', syncSharedPosts);

    return () => {
      window.removeEventListener('storage', syncSharedPosts);
      window.removeEventListener('linkedin-schedule-updated', syncSharedPosts);
    };
  }, []);

  useEffect(() => {
    const syncSharedRange = () => {
      const sharedRange = readSharedScheduleRange();
      if (!sharedRange) return;

      const nextStart = parseDateKey(sharedRange.startKey);
      const nextEnd = clampRangeEnd(nextStart, parseDateKey(sharedRange.endKey));
      setRangeStart(nextStart);
      setRangeEnd(nextEnd);
      setSelectedDay(null);
    };

    window.addEventListener('storage', syncSharedRange);
    window.addEventListener('linkedin-schedule-range-updated', syncSharedRange);
    return () => {
      window.removeEventListener('storage', syncSharedRange);
      window.removeEventListener('linkedin-schedule-range-updated', syncSharedRange);
    };
  }, []);

  useEffect(() => {
    if (!hoverTooltip) return undefined;

    const clearTooltip = () => setHoverTooltip(null);
    window.addEventListener('scroll', clearTooltip, true);
    window.addEventListener('resize', clearTooltip);

    return () => {
      window.removeEventListener('scroll', clearTooltip, true);
      window.removeEventListener('resize', clearTooltip);
    };
  }, [hoverTooltip]);

  useEffect(() => {
    const syncDraftPosts = () => {
      setDraftPosts(readScheduleDraftPosts());
      setCampaigns((prev) => applyDraftPostExclusions(prev));
    };

    window.addEventListener('storage', syncDraftPosts);
    window.addEventListener('linkedin-schedule-draft-posts-updated', syncDraftPosts);
    return () => {
      window.removeEventListener('storage', syncDraftPosts);
      window.removeEventListener('linkedin-schedule-draft-posts-updated', syncDraftPosts);
    };
  }, []);

  useEffect(() => {
    if (isDraftPanelOpen || selectedDay != null) return;
    if (selectedPostId != null && postExistsOnSchedule(campaigns, selectedPostId)) return;
    const firstPostId = findFirstScheduledPostId(campaigns);
    if (firstPostId != null) setSelectedPostId(firstPostId);
  }, [campaigns, isDraftPanelOpen, selectedDay, selectedPostId]);

  const filteredCampaigns = useMemo(
    () => applyScheduleFilters(campaigns, {
      campaignFilter,
      statusFilter,
      languageFilter,
      searchQuery,
    }),
    [campaigns, campaignFilter, statusFilter, languageFilter, searchQuery],
  );

  const scheduleDays = useMemo(
    () => buildScheduleDaysFromRange(rangeStart, rangeEnd, localeTag),
    [rangeStart, rangeEnd, localeTag],
  );

  const handleViewModeChange = (mode: 'Month' | 'Week' | 'List') => {
    const nextEnd = addDays(rangeStart, getViewDayCount(mode) - 1);
    setViewMode(mode);
    setRangeEnd(nextEnd);
    writeSharedScheduleRange({ startKey: toDateKey(rangeStart), endKey: toDateKey(nextEnd) });
    setSelectedDay(null);
  };

  const handleRangeChange = (start: Date, end: Date) => {
    const nextStart = startOfDay(start);
    const nextEnd = clampRangeEnd(nextStart, end);
    setRangeStart(nextStart);
    setRangeEnd(nextEnd);
    writeSharedScheduleRange({ startKey: toDateKey(nextStart), endKey: toDateKey(nextEnd) });
    setSelectedDay(null);
  };

  const shiftRangeByDays = (days: number) => {
    const nextStart = addDays(rangeStart, days);
    const nextEnd = addDays(rangeEnd, days);
    setRangeStart(nextStart);
    setRangeEnd(nextEnd);
    writeSharedScheduleRange({ startKey: toDateKey(nextStart), endKey: toDateKey(nextEnd) });
    setSelectedDay(null);
  };

  const scheduleMonthLabel = useMemo(() => {
    const first = scheduleDays[0]?.date;
    if (!first) return '';
    return first.toLocaleDateString(localeTag, { month: 'long', year: 'numeric' });
  }, [scheduleDays, localeTag]);

  const visibleDayKeys = useMemo(
    () => new Set(scheduleDays.map((day) => day.key)),
    [scheduleDays],
  );

  const isScrollableSchedule = scheduleDays.length > SCHEDULE_HORIZONTAL_SCROLL_MIN_DAYS;
  const dayColumnClass = isScrollableSchedule ? 'min-w-[72px] shrink-0' : 'flex-1';

  const handleScheduleBodyScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!scheduleHeaderRef.current) return;
    scheduleHeaderRef.current.scrollLeft = event.currentTarget.scrollLeft;
  };

  const handleScheduleGridMouseLeave = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isNodeInsideScheduleGrid(event.relatedTarget, scheduleBodyRef.current)) return;
    setHoverTooltip(null);
  };

  const goToToday = () => {
    const today = startOfDay(new Date());
    const dayCount = getViewDayCount(viewMode);
    setRangeStart(addDays(today, -Math.floor(dayCount / 2)));
    setRangeEnd(addDays(today, -Math.floor(dayCount / 2) + dayCount - 1));
    setSelectedDay(null);
  };

  const goSchedulePrev = () => {
    shiftRangeByDays(-getScheduleStep(viewMode));
  };

  const goScheduleNext = () => {
    shiftRangeByDays(getScheduleStep(viewMode));
  };

  useEffect(() => {
    if (selectedDay && !visibleDayKeys.has(selectedDay)) {
      setSelectedDay(null);
    }
  }, [selectedDay, visibleDayKeys]);

  const totalScheduled = campaigns.reduce((sum, c) => sum + Object.values(c.schedule).flat().length, 0);

  const allPosts = campaigns.flatMap((c) => Object.values(c.schedule).flat());
  const draftsCount = allPosts.filter((p) => p.status === 'Draft').length;
  const activeCampaigns = campaigns.filter((c) => c.status === 'Active').length;
  const pausedCampaigns = campaigns.filter((c) => c.status === 'Paused').length;
  const upcomingPosts = allPosts.filter((p) => p.status === 'Scheduled').length;

  const selectedPost = useMemo(() => {
    if (selectedPostId == null) return null;

    for (const campaign of campaigns) {
      for (const [day, posts] of Object.entries(campaign.schedule)) {
        const post = posts.find((p) => p.id === selectedPostId);
        if (!post) continue;

        return {
          ...DEFAULT_POST_DETAIL,
          id: post.id,
          title: post.title,
          status: post.status,
          language: resolvePostLanguage(post),
          scheduledDateKey: day,
          scheduledTime: post.time,
          campaignName: campaign.name,
          accountName: CAMPAIGN_ACCOUNTS[campaign.id] ?? t('linkedInSchedule.linkedInAccount'),
          scheduledDate: formatScheduleDayDate(day, localeTag),
          description: `Post "${post.title}" is prepared for ${campaign.name}. Click schedule to publish at the planned time.`,
          objective: `Increase engagement for ${campaign.name}.`,
          approvalStatus:
            post.status === 'Approved' || post.status === 'Posted' ? 'Approved' : 'Pending',
        };
      }
    }

    return null;
  }, [campaigns, selectedPostId, t, localeTag]);

  const selectedDayPosts = useMemo(() => {
    if (!selectedDay) return [];

    const rows: DayPostRow[] = [];
    filteredCampaigns.forEach((campaign) => {
      (campaign.schedule[selectedDay] ?? []).forEach((post) => {
        rows.push({
          post,
          campaignId: campaign.id,
          campaignName: campaign.name,
          campaignType: campaign.type,
          accountName: CAMPAIGN_ACCOUNTS[campaign.id] ?? t('linkedInSchedule.linkedInAccount'),
          hasConflict: hasScheduleConflict(campaigns, selectedDay, post),
        });
      });
    });

    return rows.sort((a, b) => a.post.time.localeCompare(b.post.time));
  }, [campaigns, filteredCampaigns, selectedDay, t]);

  const handleSelectDay = (day: string) => {
    setSelectedDay((prev) => (prev === day ? null : day));
    setSelectedPostId(null);
    setIsDraftPanelOpen(false);
  };

  const handleSelectPost = (postId: number) => {
    setSelectedPostId(postId);
    setSelectedDay(null);
    setIsDraftPanelOpen(false);
  };

  const handleCloseDetailPanel = () => {
    setSelectedDay(null);
    setIsDraftPanelOpen(false);
    const firstPostId = findFirstScheduledPostId(campaigns);
    setSelectedPostId(firstPostId);
  };

  const persistDraftPosts = (posts: ScheduleDraftPost[]) => {
    setDraftPosts(posts);
    writeScheduleDraftPosts(posts);
  };

  const handleToggleDraftPanel = () => {
    setHoverTooltip(null);
    setIsDraftPanelOpen((open) => {
      const nextOpen = !open;
      if (nextOpen) {
        setSelectedDay(null);
      } else {
        const firstPostId = findFirstScheduledPostId(campaigns);
        if (firstPostId != null) setSelectedPostId(firstPostId);
      }
      return nextOpen;
    });
  };

  const handleRemoveDraftPost = (postId: number) => {
    persistDraftPosts(draftPosts.filter((post) => post.id !== postId));
  };

  const handleDraftPanelDragOver = (
    event: DragEvent<HTMLElement>,
    target: DraftDropTarget,
  ) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDraftDropTarget(target);
  };

  const handleDraftPanelDragLeave = (event: DragEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget as Node | null;
    if (nextTarget && event.currentTarget.contains(nextTarget)) return;
    setDraftDropTarget(null);
  };

  const handleDraftPanelDrop = (
    event: DragEvent<HTMLElement>,
    target: DraftDropTarget,
  ) => {
    event.preventDefault();
    setDraftDropTarget(null);

    if (draggedDraftPostId != null) {
      const draftPost = draftPosts.find((post) => post.id === draggedDraftPostId);
      if (!draftPost) {
        clearScheduleInteractionState();
        return;
      }
      persistDraftPosts(insertDraftPostAtTarget(draftPosts, draftPost, target));
      clearScheduleInteractionState();
      return;
    }

    if (!draggedPost) return;

    const moved = movePostFromScheduleToDraft(campaigns, draggedPost);
    if (!moved) {
      clearScheduleInteractionState();
      return;
    }

    const nextDraftPosts = insertDraftPostAtTarget(draftPosts, moved.draftPost, target);
    const draftIds = new Set(nextDraftPosts.map((post) => post.id));
    const nextCampaigns = removePostsFromCampaignsByIds(moved.nextCampaigns, draftIds);
    const layoutSnapshot = cloneCampaignRows(nextCampaigns);

    setCampaigns(nextCampaigns);
    savedScheduleRef.current = layoutSnapshot;
    writeSavedScheduleLayout(layoutSnapshot);
    persistDraftPosts(nextDraftPosts);
    removeSharedScheduledPost(moved.draftPost.id);
    setIsDraftPanelOpen(true);
    setSelectedDay(null);
    if (selectedPostId === draggedPost.postId) {
      setSelectedPostId(findFirstScheduledPostId(nextCampaigns));
    }
    clearScheduleInteractionState();
  };

  const showRightPanel = true;

  const handleDragDraftStart = (postId: number) => {
    setHoverTooltip(null);
    setDraggedPost(null);
    setDraggedDraftPostId(postId);
    setDropTarget(null);
  };

  const handleDropDraftPost = (target: DropTarget) => {
    if (draggedDraftPostId == null) return;

    const draftPost = draftPosts.find((post) => post.id === draggedDraftPostId);
    if (!draftPost) {
      clearScheduleInteractionState();
      return;
    }

    const targetCampaign = campaigns.find((campaign) => campaign.id === target.campaignId);
    if (!targetCampaign) {
      clearScheduleInteractionState();
      return;
    }

    const scheduledPost = draftPostToScheduledPost(draftPost);

    setCampaigns((prev) => {
      const nextCampaigns = insertDraftPostIntoSchedule(prev, scheduledPost, target);
      const layoutSnapshot = cloneCampaignRows(nextCampaigns);
      savedScheduleRef.current = layoutSnapshot;
      writeSavedScheduleLayout(layoutSnapshot);
      return nextCampaigns;
    });
    persistDraftPosts(draftPosts.filter((post) => post.id !== draftPost.id));
    restoreDraftPostToSharedStorage(draftPost, targetCampaign, target.day, scheduledPost.id);
    setSelectedPostId(scheduledPost.id);
    setSelectedDay(null);
    setIsDraftPanelOpen(true);
    clearScheduleInteractionState();
  };

  const handleDropPost = (target: DropTarget) => {
    if (draggedDraftPostId != null) {
      handleDropDraftPost(target);
      return;
    }
    if (!draggedPost) return;

    const sourceCampaign = campaigns.find((campaign) => campaign.id === draggedPost.sourceCampaignId);
    const sourcePosts = sourceCampaign?.schedule[draggedPost.sourceDay] ?? [];
    if (isDropNoOp(sourcePosts, draggedPost, target)) {
      clearScheduleInteractionState();
      return;
    }

    setCampaigns((prev) => {
      const nextCampaigns = moveScheduledPost(prev, draggedPost, target);
      syncSharedPostAfterMove(nextCampaigns, draggedPost.postId, target.campaignId, target.day);
      return nextCampaigns;
    });
    setSelectedPostId(draggedPost.postId);
    setSelectedDay((prev) => (prev === target.day ? prev : null));
    clearScheduleInteractionState();
  };

  const handlePostStatusChange = (status: PostStatus) => {
    if (selectedPostId == null) return;

    setCampaigns((prev) => {
      const nextCampaigns = updatePostStatusInCampaigns(prev, selectedPostId, status);
      syncSharedPostStatus(nextCampaigns, selectedPostId);
      return nextCampaigns;
    });
  };

  const updateSelectedPostMetadata = (updates: {
    title?: string;
    time?: string;
    dayKey?: string;
    language?: string;
  }) => {
    if (selectedPostId == null) return;

    setCampaigns((prev) => {
      const nextCampaigns = updatePostMetadataInCampaigns(prev, selectedPostId, updates);
      syncSharedPostStatus(nextCampaigns, selectedPostId);
      return nextCampaigns;
    });
  };

  const clearScheduleInteractionState = () => {
    setHoverTooltip(null);
    setDraggedPost(null);
    setDraggedDraftPostId(null);
    setDropTarget(null);
    setDraftDropTarget(null);
  };

  const handleSaveScheduleLayout = () => {
    const snapshot = cloneCampaignRows(campaigns);
    savedScheduleRef.current = snapshot;
    writeSavedScheduleLayout(snapshot);
  };

  const handleRestoreSavedScheduleLayout = () => {
    const snapshot = cloneCampaignRows(savedScheduleRef.current);
    setCampaigns(snapshot);
    clearScheduleInteractionState();
  };

  return (
    <div className="relative flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-[rgb(224_231_255/0.6)]">
      <div className="pointer-events-none absolute -left-24 top-20 h-72 w-72 rounded-full bg-blue-400/20 blur-3xl" aria-hidden />
      <div className="pointer-events-none absolute bottom-0 right-0 h-80 w-80 rounded-full bg-indigo-400/15 blur-3xl" aria-hidden />

      {/* ── Header ── */}
      <div className={`relative z-10 shrink-0 px-6 py-5 ${GLASS_FRAME} border-b border-white/30`}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-800 sm:text-3xl hawk-gradient-text">{t('linkedInSchedule.title')}</h1>
            <p className="mt-0.5 text-sm text-slate-500">{t('linkedInSchedule.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              className={`${HEADER_ACTION_BUTTON_BASE} ${HEADER_ACTION_BUTTON_COLORS.auto}`}
              onClick={handleRestoreSavedScheduleLayout}
            >
              <Zap className="h-3.5 w-3.5" /> {t('linkedInSchedule.autoSchedule')}
            </Button>
            <Button size="sm" className={`${HEADER_ACTION_BUTTON_BASE} ${HEADER_ACTION_BUTTON_COLORS.campaign}`}>
              <Plus className="h-3.5 w-3.5" /> {t('linkedInSchedule.newCampaign')}
            </Button>
            <Button size="sm" className={`${HEADER_ACTION_BUTTON_BASE} ${HEADER_ACTION_BUTTON_COLORS.post}`}>
              <Plus className="h-3.5 w-3.5" /> {t('linkedInSchedule.newPost')}
            </Button>
          </div>
        </div>

        {/* ── Filter Bar ── */}
        <div
          className="mt-4 flex flex-wrap items-center gap-3"
          data-exclusive-select-group={toolbarSelectGroupId}
        >
          <Select
            value={campaignFilter}
            open={openToolbarDropdown === 'campaign'}
            onOpenChange={onToolbarOpenChange('campaign')}
            onValueChange={(value) => {
              setCampaignFilter(value);
              setOpenToolbarDropdown(null);
            }}
          >
            <SelectTrigger
              data-exclusive-select-id="campaign"
              onPointerDown={onToolbarTriggerPointerDown('campaign')}
              className={`${toolbarSelectTriggerClass} h-9 w-[160px] bg-white text-xs`}
            >
              <SelectValue placeholder={t('linkedInSchedule.allCampaigns')} />
            </SelectTrigger>
            <SelectContent onPointerDownOutside={onToolbarSelectContentOutside}>
              <SelectItem value="all">{t('linkedInSchedule.allCampaigns')}</SelectItem>
              {campaigns.map((c) => (
                <SelectItem key={c.id} value={c.id.toString()}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={statusFilter}
            open={openToolbarDropdown === 'status'}
            onOpenChange={onToolbarOpenChange('status')}
            onValueChange={(value) => {
              setStatusFilter(value);
              setOpenToolbarDropdown(null);
            }}
          >
            <SelectTrigger
              data-exclusive-select-id="status"
              onPointerDown={onToolbarTriggerPointerDown('status')}
              className={`${toolbarSelectTriggerClass} h-9 w-[130px] bg-white text-xs`}
            >
              <SelectValue placeholder={t('linkedInSchedule.allStatus')} />
            </SelectTrigger>
            <SelectContent onPointerDownOutside={onToolbarSelectContentOutside}>
              <SelectItem value="all">{t('linkedInSchedule.allStatus')}</SelectItem>
              <SelectItem value="Active">{t('campaigns.status.active')}</SelectItem>
              <SelectItem value="Paused">{t('common.paused')}</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={languageFilter}
            open={openToolbarDropdown === 'language'}
            onOpenChange={onToolbarOpenChange('language')}
            onValueChange={(value) => {
              setLanguageFilter(value);
              setOpenToolbarDropdown(null);
            }}
          >
            <SelectTrigger
              data-exclusive-select-id="language"
              onPointerDown={onToolbarTriggerPointerDown('language')}
              className={`${toolbarSelectTriggerClass} h-9 w-[140px] bg-white text-xs`}
            >
              {languageFilter === 'all' ? (
                <SelectValue placeholder={t('linkedInSchedule.allLanguages')} />
              ) : (
                <span className="flex items-center gap-2">
                  <LanguageFlagByCode code={languageFilter as LanguageCode} />
                  <span className="truncate">{LANGUAGE_OPTIONS[languageFilter as LanguageCode].label}</span>
                </span>
              )}
            </SelectTrigger>
            <SelectContent onPointerDownOutside={onToolbarSelectContentOutside}>
              <SelectItem value="all">{t('linkedInSchedule.allLanguages')}</SelectItem>
              {(Object.keys(LANGUAGE_OPTIONS) as LanguageCode[]).map((code) => (
                <SelectItem key={code} value={code}>
                  <span className="flex items-center gap-2">
                    <LanguageFlagByCode code={code} />
                    <span>{LANGUAGE_OPTIONS[code].label}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <DateRangePicker
            start={rangeStart}
            end={rangeEnd}
            onRangeChange={handleRangeChange}
            open={openToolbarDropdown === 'date'}
            onOpenChange={onToolbarOpenChange('date')}
            exclusiveSelectId="date"
            exclusiveSelectTriggerClass={toolbarSelectTriggerClass}
            onTriggerPointerDown={onToolbarTriggerPointerDown('date')}
          />

          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder={t('linkedInSchedule.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`h-9 w-[240px] rounded-lg pl-9 pr-3 text-xs focus:border-blue-300/60 focus:outline-none focus:ring-1 focus:ring-blue-200/80 ${GLASS_INPUT}`}
            />
          </div>

          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-9 text-xs" onClick={goToToday}>
              {t('common.today')}
            </Button>
            <button
              type="button"
              onClick={goSchedulePrev}
              className="rounded p-1 text-slate-400 hover:bg-slate-100"
              aria-label={t('linkedInSchedule.prevDays')}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={goScheduleNext}
              className="rounded p-1 text-slate-400 hover:bg-slate-100"
              aria-label={t('linkedInSchedule.nextDays')}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <span className="hidden text-sm font-semibold text-slate-700 sm:inline">{scheduleMonthLabel}</span>
          </div>
        </div>
      </div>

      {/* ── Stats Cards ── */}
      <div className="relative z-10 shrink-0 px-6 py-4">
        <div className="grid grid-cols-5 gap-4">
          <StatCard icon={Calendar} iconBg="bg-blue-100" iconColor="text-blue-600" label={t('linkedInSchedule.statTotalScheduled')} value={totalScheduled} sub={t('linkedInSchedule.statGrowth')} subColor="text-green-600" />
          <StatCard icon={FileText} iconBg="bg-amber-100" iconColor="text-amber-600" label={t('linkedInSchedule.statDraftsPending')} value={draftsCount} sub={t('linkedInSchedule.statNeedsReview')} subColor="text-amber-600" />
          <StatCard icon={RefreshCcw} iconBg="bg-green-100" iconColor="text-green-600" label={t('linkedInSchedule.statActiveCampaigns')} value={activeCampaigns} sub={t('linkedInSchedule.statRunningPaused', { active: activeCampaigns, paused: pausedCampaigns })} />
          <StatCard icon={Clock} iconBg="bg-purple-100" iconColor="text-purple-600" label={t('linkedInSchedule.statUpcomingAutoPosts')} value={upcomingPosts} sub={t('linkedInSchedule.statNextPost')} />
          <StatCard icon={BarChart3} iconBg="bg-cyan-100" iconColor="text-cyan-600" label={t('linkedInSchedule.statPostingFrequency')} value={t('linkedInSchedule.statFrequencyValue')} sub={t('linkedInSchedule.statOptimal')} subColor="text-green-600" />
        </div>
      </div>

      {/* ── Main Content: Schedule Grid + Post Details ── */}
      <div className="relative z-10 flex min-h-0 flex-1 overflow-hidden px-4 pb-4">
        {/* ── Schedule Grid ── */}
        <div className={`mx-1 flex min-w-0 flex-1 flex-col overflow-hidden rounded-2xl ${GLASS_FRAME}`}>
          {/* Grid Header */}
          <div
            ref={scheduleHeaderRef}
            className={`flex shrink-0 border-b border-white/30 bg-white/20 backdrop-blur-md ${isScrollableSchedule ? 'overflow-x-hidden' : ''}`}
          >
            <div className="flex min-w-max flex-1">
            <div className="sticky left-0 z-30 flex w-[220px] shrink-0 items-center gap-2 border-r border-white/30 bg-[rgb(232_238_255/0.92)] px-4 py-2.5 backdrop-blur-md">
              <span className="text-xs font-semibold text-slate-700">{t('linkedInSchedule.gridCampaigns')}</span>
              <button type="button" className="rounded p-0.5 text-slate-400 hover:bg-slate-100"><Settings className="h-3.5 w-3.5" /></button>
            </div>
            {scheduleDays.map((day) => {
              const isSelectedDay = selectedDay === day.key;
              return (
                <button
                  key={day.key}
                  type="button"
                  onClick={() => handleSelectDay(day.key)}
                  className={`flex items-center justify-center border-r border-white/25 py-2.5 transition-colors last:border-r-0 hover:bg-white/30 ${
                    dayColumnClass
                  } ${isSelectedDay ? 'bg-blue-400/15 ring-2 ring-inset ring-blue-300/50 backdrop-blur-sm' : ''}`}
                >
                  <span className={`text-xs font-medium ${day.isToday ? 'text-blue-600' : 'text-slate-500'}`}>
                    {day.weekday}{' '}
                    {day.isToday ? (
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white">{day.dayNum}</span>
                    ) : (
                      <span className={`font-bold ${isSelectedDay ? 'text-blue-700' : 'text-slate-700'}`}>{day.dayNum}</span>
                    )}
                  </span>
                </button>
              );
            })}
            </div>
          </div>

          {/* Grid Body */}
          <div
            ref={scheduleBodyRef}
            onScroll={handleScheduleBodyScroll}
            onMouseLeave={handleScheduleGridMouseLeave}
            className={`flex-1 overflow-y-auto ${isScrollableSchedule ? 'hawk-schedule-body-scroll overflow-x-auto' : 'overflow-x-hidden scrollbar-hide'}`}
          >
            <AnimatePresence initial={false}>
              {filteredCampaigns.map((campaign) => {
                const rowStats = getCampaignRowStats(campaign);
                const approvalPercent =
                  rowStats.totalPosts > 0
                    ? Math.min((rowStats.approvedCount / rowStats.totalPosts) * 100, 100)
                    : 0;

                return (
                <motion.div
                  key={campaign.id}
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className={`flex ${isScrollableSchedule ? 'min-w-max' : ''}`}
                >
                  {/* Campaign Info */}
                  <div className="sticky left-0 z-20 flex w-[220px] shrink-0 flex-col justify-center border-b border-r border-b-slate-300/80 border-r-white/30 bg-[rgb(232_238_255/0.92)] px-4 py-3 backdrop-blur-md">
                    <div className="flex items-center gap-2">
                      <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-blue-100">
                        <LinkedInLogo className="h-3.5 w-3.5 object-contain" />
                      </div>
                      <p className="truncate text-sm font-semibold text-slate-900">{campaign.name}</p>
                      <Badge className={`shrink-0 border text-[9px] font-medium ${campaign.status === 'Active' ? 'border-green-200 bg-green-100 text-green-700' : 'border-red-200 bg-red-100 text-red-700'}`}>
                        {translateCampaignStatus(campaign.status, t)}
                      </Badge>
                    </div>
                    <p className="mt-0.5 truncate text-[11px] text-slate-500">{campaign.type}</p>
                    <div className="mt-1.5 flex items-center gap-2">
                      <span className="shrink-0 text-[11px] text-slate-500">
                        {t('linkedInSchedule.postsProgress', { approved: rowStats.approvedCount, total: rowStats.totalPosts })}
                      </span>
                      <div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-slate-200/80">
                        <div
                          className={`h-full rounded-full transition-all duration-300 ${getProgressBarColor(approvalPercent)}`}
                          style={{ width: `${approvalPercent}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Day Cells */}
                  <div className={`flex border-b border-b-slate-300/80 ${isScrollableSchedule ? '' : 'min-w-0 flex-1'}`}>
                  {scheduleDays.map((day) => {
                    const dayKey = day.key;
                    const posts = campaign.schedule[dayKey] ?? [];
                    const isCellActive =
                      dropTarget?.campaignId === campaign.id && dropTarget.day === dayKey;
                    const isCellEndTarget = isCellActive && dropTarget?.position === 'end';

                    return (
                      <div
                        key={dayKey}
                        onDragOver={(event) => {
                          event.preventDefault();
                          event.dataTransfer.dropEffect = 'move';
                          setDropTarget({
                            campaignId: campaign.id,
                            day: dayKey,
                            position: 'end',
                          });
                        }}
                        onDragLeave={(event) => {
                          const nextTarget = event.relatedTarget as Node | null;
                          if (nextTarget && event.currentTarget.contains(nextTarget)) return;
                          if (dropTarget?.campaignId === campaign.id && dropTarget.day === dayKey) {
                            setDropTarget(null);
                          }
                        }}
                        onDrop={(event) => {
                          event.preventDefault();
                          handleDropPost(buildScheduleDropTarget(campaign.id, dayKey, dropTarget));
                        }}
                        className={`flex min-h-[100px] flex-col gap-1.5 border-r border-white/20 px-1.5 py-2 transition-colors last:border-r-0 ${dayColumnClass} ${
                          isCellEndTarget
                            ? 'bg-blue-400/20 ring-2 ring-inset ring-blue-300/50 backdrop-blur-sm'
                            : isCellActive
                              ? 'bg-blue-400/10 backdrop-blur-sm'
                              : ''
                        }`}
                      >
                        {posts.map((post) => {
                          const style = POST_CARD_STYLE[post.status];
                          const isSelected = selectedPostId === post.id;
                          const isDragging = draggedPost?.postId === post.id;
                          const isDropBefore =
                            dropTarget?.referencePostId === post.id && dropTarget.position === 'before';
                          const isDropAfter =
                            dropTarget?.referencePostId === post.id && dropTarget.position === 'after';
                          const accountName = CAMPAIGN_ACCOUNTS[campaign.id] ?? 'LinkedIn Account';
                          const hasConflict = hasScheduleConflict(campaigns, dayKey, post);

                          return (
                            <div
                              key={post.id}
                              className="relative w-full"
                              onMouseEnter={(event) => {
                                if (draggedPost || draggedDraftPostId != null || isDragging) return;
                                setHoverTooltip({
                                  accountName,
                                  post,
                                  campaignName: campaign.name,
                                  campaignType: campaign.type,
                                  day: dayKey,
                                  hasConflict,
                                  anchorRect: event.currentTarget.getBoundingClientRect(),
                                });
                              }}
                              onDragOver={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                if (draggedPost?.postId === post.id || draggedDraftPostId === post.id) return;
                                event.dataTransfer.dropEffect = 'move';
                                const position = getCardDropPosition(event);
                                setDropTarget({
                                  campaignId: campaign.id,
                                  day: dayKey,
                                  referencePostId: post.id,
                                  position,
                                });
                              }}
                              onDrop={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                if (draggedPost?.postId === post.id || draggedDraftPostId === post.id) return;
                                handleDropPost(
                                  buildScheduleDropTarget(campaign.id, dayKey, dropTarget, post.id, event),
                                );
                              }}
                            >
                              {isDropBefore && (
                                <div className="pointer-events-none absolute -top-1 left-0 right-0 z-10 h-0.5 rounded-full bg-blue-500 shadow-[0_0_0_1px_rgba(59,130,246,0.35)]" />
                              )}
                              <button
                                type="button"
                                draggable
                                onDragStart={() => {
                                  setHoverTooltip(null);
                                  setDraggedDraftPostId(null);
                                  setDraggedPost({
                                    postId: post.id,
                                    sourceCampaignId: campaign.id,
                                    sourceDay: dayKey,
                                  });
                                  setDropTarget(null);
                                }}
                                onDragOver={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  event.dataTransfer.dropEffect = 'move';
                                  if (draggedPost?.postId === post.id || draggedDraftPostId === post.id) return;

                                  const position = getCardDropPosition(event);
                                  setDropTarget({
                                    campaignId: campaign.id,
                                    day: dayKey,
                                    referencePostId: post.id,
                                    position,
                                  });
                                }}
                                onDrop={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  if (draggedPost?.postId === post.id || draggedDraftPostId === post.id) return;

                                  handleDropPost(
                                    buildScheduleDropTarget(campaign.id, dayKey, dropTarget, post.id, event),
                                  );
                                }}
                                onDragEnd={clearScheduleInteractionState}
                                onClick={() => handleSelectPost(post.id)}
                                className={`relative flex min-h-[100px] w-full flex-col rounded-lg border px-3 py-3 text-left shadow-[0_2px_12px_rgba(15,23,42,0.05)] transition-all ${style.bg} ${style.border} ${
                                  isDragging ? 'opacity-35' : ''
                                } ${
                                  isDropBefore || isDropAfter
                                    ? 'ring-2 ring-blue-400/70 ring-offset-1'
                                    : isSelected
                                      ? 'ring-2 ring-blue-400/70 ring-offset-1'
                                      : 'hover:border-white/70 hover:shadow-[0_6px_20px_rgba(15,23,42,0.1)]'
                                }`}
                              >
                                <div className="flex w-full flex-1 items-start gap-3">
                                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-blue-600">
                                    <LinkedInLogo className="h-5 w-5 object-contain" />
                                  </div>
                                  <p className={`min-w-0 flex-1 line-clamp-3 text-sm font-semibold leading-snug ${style.label}`}>{post.title}</p>
                                  {post.status === 'Approved' && (
                                    <CheckCircle2 className="h-5 w-5 shrink-0 text-green-500" strokeWidth={2.4} />
                                  )}
                                  {post.status === 'Scheduled' && (
                                    <CheckCircle2 className="h-5 w-5 shrink-0 text-amber-500" strokeWidth={2.4} />
                                  )}
                                </div>
                                <div className="mt-auto flex w-full items-center gap-2 border-t border-white/40 pt-2">
                                  <span className={`h-3 w-3 shrink-0 rounded-full ${STATUS_DOT_COLOR[post.status]}`} />
                                  <span className="truncate text-sm font-medium text-slate-600">{post.time}</span>
                                </div>
                              </button>
                              {isDropAfter && (
                                <div className="pointer-events-none absolute -bottom-1 left-0 right-0 z-10 h-0.5 rounded-full bg-blue-500 shadow-[0_0_0_1px_rgba(59,130,246,0.35)]" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                  </div>
                </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          {/* Grid Footer */}
          <div className="flex shrink-0 items-center justify-between border-t border-white/30 bg-white/20 px-4 py-2.5 backdrop-blur-md">
            <div className="flex items-center gap-2 text-[11px] text-slate-500">
              <AlertCircle className="h-3.5 w-3.5" />
              {t('linkedInSchedule.dragHint')}
            </div>
            <div className="flex items-center gap-1.5">
              {(['Month', 'Week', 'List'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => handleViewModeChange(mode)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === mode ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-100'}`}
                >
                  {t(VIEW_MODE_I18N[mode])}
                </button>
              ))}
              <Button
                type="button"
                size="sm"
                className={`ml-2 ${HEADER_ACTION_BUTTON_BASE} ${HEADER_ACTION_BUTTON_COLORS.draft} ${
                  isDraftPanelOpen ? 'ring-2 ring-amber-300 ring-offset-1' : ''
                }`}
                onClick={handleToggleDraftPanel}
              >
                <FileText className="h-3.5 w-3.5" /> {t('linkedInSchedule.draft')}
              </Button>
              <Button
                type="button"
                size="sm"
                className={`${HEADER_ACTION_BUTTON_BASE} ${HEADER_ACTION_BUTTON_COLORS.save}`}
                onClick={handleSaveScheduleLayout}
              >
                <Save className="h-3.5 w-3.5" /> {t('common.save')}
              </Button>
            </div>
          </div>
        </div>

        {/* ── Right Panel: Post / Day Details ── */}
        {showRightPanel && (
          <div
            className={`group/detail relative ml-3 flex min-h-0 w-[300px] shrink-0 flex-col overflow-hidden rounded-2xl ${GLASS_PANEL} shadow-[0_8px_32px_rgba(15,23,42,0.1)]`}
            onMouseEnter={() => setHoverTooltip(null)}
          >
            <div className="flex items-center justify-between border-b  rounded-t-2xl border-white/30 bg-white/20 px-4 py-3 backdrop-blur-md">
              <h3 className="text-sm font-semibold text-slate-900">
                {isDraftPanelOpen
                  ? t('linkedInSchedule.draft')
                  : selectedPost
                    ? t('linkedInSchedule.postDetails')
                    : t('linkedInSchedule.dayDetails', { day: formatDayKeyLabel(selectedDay ?? '', localeTag) })}
              </h3>
              <button type="button" onClick={handleCloseDetailPanel} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                ×
              </button>
            </div>

            {isDraftPanelOpen ? (
              <ScheduleDraftPanel
                posts={draftPosts}
                draggedDraftPostId={draggedDraftPostId}
                dropTarget={draftDropTarget}
                onDragOver={handleDraftPanelDragOver}
                onDragLeave={handleDraftPanelDragLeave}
                onDrop={handleDraftPanelDrop}
                onDragDraftStart={handleDragDraftStart}
                onDragDraftEnd={clearScheduleInteractionState}
                onRemove={handleRemoveDraftPost}
              />
            ) : selectedPost ? (
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="min-h-0 flex-1 overflow-y-auto scrollbar-hide">
              <div className="space-y-4 px-4 pb-4 pt-4">
                {/* Post Title */}
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-slate-500">{t('linkedInSchedule.postTitle')}</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={selectedPost.title}
                      maxLength={100}
                      onChange={(event) => updateSelectedPostMetadata({ title: event.target.value })}
                      className={`w-full rounded-lg px-3 py-2 text-sm font-medium text-slate-900 focus:border-blue-300/60 focus:outline-none focus:ring-1 focus:ring-blue-200/80 ${GLASS_INPUT}`}
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">
                      {selectedPost.title.length} / 100
                    </span>
                  </div>
                </div>

                {/* Status */}
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-slate-500">{t('common.status')}</label>
                  <Select
                    value={selectedPost.status}
                    onValueChange={(value) => handlePostStatusChange(value as PostStatus)}
                  >
                    <SelectTrigger className={`h-8 w-full rounded-lg px-3 text-xs font-medium text-slate-900 focus:border-blue-300/60 focus:outline-none focus:ring-1 focus:ring-blue-200/80 ${GLASS_INPUT}`}>
                      <span className="flex items-center gap-2">
                        <span className={`h-2 w-2 rounded-full ${STATUS_DOT_COLOR[selectedPost.status]}`} />
                        <span className={`text-sm font-semibold ${STATUS_TEXT_COLOR[selectedPost.status]}`}>
                          {translatePostStatus(selectedPost.status, t)}
                        </span>
                      </span>
                    </SelectTrigger>
                    <SelectContent>
                      {(['Draft', 'Scheduled', 'Approved', 'Posted'] as PostStatus[]).map((s) => (
                        <SelectItem key={s} value={s}>
                          <span className="flex items-center gap-2">
                            <span className={`h-2 w-2 rounded-full ${STATUS_DOT_COLOR[s]}`} />
                            <span className={`text-sm font-semibold ${STATUS_TEXT_COLOR[s]}`}>{translatePostStatus(s, t)}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Language */}
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-slate-500">{t('linkedInSchedule.language')}</label>
                  <Select
                    value={languageCodeFromLabel(selectedPost.language)}
                    onValueChange={(code) =>
                      updateSelectedPostMetadata({
                        language: LANGUAGE_OPTIONS[code as LanguageCode].label,
                      })
                    }
                  >
                    <SelectTrigger className={`h-8 w-full rounded-lg px-3 text-xs font-medium text-slate-900 ${GLASS_INPUT}`}>
                      <span className="flex items-center gap-2">
                        <LanguageFlagByCode code={languageCodeFromLabel(selectedPost.language)} />
                        <span className="truncate">{selectedPost.language}</span>
                      </span>
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(LANGUAGE_OPTIONS) as LanguageCode[]).map((code) => (
                        <SelectItem key={code} value={code}>
                          <span className="flex items-center gap-2">
                            <LanguageFlagByCode code={code} />
                            <span>{LANGUAGE_OPTIONS[code].label}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Scheduled Date & Time */}
                <div>
                  <label className="text-[11px] font-medium text-slate-500">{t('linkedInSchedule.scheduledDateTime')}</label>
                  <div className="mt-1 flex items-center gap-2">
                    <label
                      htmlFor="post-scheduled-date"
                      className={`hawk-native-datetime-field flex flex-1 cursor-pointer items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs text-slate-700 ${GLASS_INPUT}`}
                    >
                      <Calendar className="pointer-events-none h-3 w-3 shrink-0 text-slate-400" />
                      <input
                        id="post-scheduled-date"
                        type="date"
                        value={selectedPost.scheduledDateKey}
                        onChange={(event) => updateSelectedPostMetadata({ dayKey: event.target.value })}
                        className={`hawk-native-datetime-input--in-field text-xs text-slate-700 ${NATIVE_DATETIME_INPUT}`}
                      />
                    </label>
                    <label
                      htmlFor="post-scheduled-time"
                      className={`hawk-native-datetime-field flex flex-1 cursor-pointer items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs text-slate-700 ${GLASS_INPUT}`}
                    >
                      <Clock className="pointer-events-none h-3 w-3 shrink-0 text-slate-400" />
                      <input
                        id="post-scheduled-time"
                        type="time"
                        value={toTimeInputValue(selectedPost.scheduledTime)}
                        onChange={(event) =>
                          updateSelectedPostMetadata({ time: formatTimeFromInput(event.target.value) })
                        }
                        className={`hawk-native-datetime-input--in-field text-xs text-slate-700 ${NATIVE_DATETIME_INPUT}`}
                      />
                    </label>
                  </div>
                </div>

                {/* Campaign */}
                <div>
                  <label className="text-[11px] font-medium text-slate-500">{t('linkedInSchedule.campaign')}</label>
                  <div className="mt-1 flex items-center gap-1.5">
                    <LinkedInLogo className="h-3.5 w-3.5 object-contain" />
                    <span className="text-sm text-slate-700">{selectedPost.campaignName}</span>
                  </div>
                </div>

                {/* Objective */}
                <div>
                  <label className="text-[11px] font-medium text-slate-500">{t('linkedInSchedule.objectiveCta')}</label>
                  <p className="mt-0.5 text-sm text-slate-700">{selectedPost.objective}</p>
                </div>

                {/* Approval */}
                <div>
                  <label className="text-[11px] font-medium text-slate-500">{t('linkedInSchedule.approval')}</label>
                  <div className="mt-1 flex items-center gap-2">
                    <Badge className={`text-[10px] font-medium ${selectedPost.approvalStatus === 'Approved' ? 'border-green-200 bg-green-100 text-green-700' : 'border-amber-200 bg-amber-100 text-amber-700'}`}>
                      {translateApprovalStatus(selectedPost.approvalStatus, t)}
                    </Badge>
                    <div className="flex items-center gap-1.5">
                      <div className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 text-[9px] font-bold text-blue-700">
                        {selectedPost.accountName.charAt(0)}
                      </div>
                      <span className="text-xs text-slate-600">{selectedPost.accountName}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions — expand on hover; stays in layout so scroll area keeps full height when collapsed */}
            <div className="max-h-0 shrink-0 overflow-hidden transition-[max-height] duration-300 ease-out group-hover/detail:max-h-56 group-focus-within/detail:max-h-56">
              <div className="space-y-2 rounded-b-2xl border-t border-white/30 bg-white/90 px-4 py-4 shadow-[0_-10px_28px_rgba(15,23,42,0.1)] backdrop-blur-md">
              <Button className="w-full gap-1.5 bg-blue-600 text-xs text-white hover:bg-blue-700">
                <Send className="h-3.5 w-3.5" /> {t('linkedInSchedule.schedule')}
              </Button>
              <Button variant="outline" className="w-full gap-1.5 text-xs">
                <Eye className="h-3.5 w-3.5" /> {t('linkedInSchedule.publishNow')}
              </Button>
              <Button variant="outline" className="w-full gap-1.5 text-xs">
                <Save className="h-3.5 w-3.5" /> {t('linkedInSchedule.saveDraft')}
              </Button>
              <button type="button" className="flex w-full items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-red-500 hover:text-red-600">
                <Trash2 className="h-3.5 w-3.5" /> {t('linkedInSchedule.deletePost')}
              </button>
              </div>
            </div>
              </div>
            ) : selectedDay ? (
              <DayPostsDetailPanel
                day={selectedDay}
                rows={selectedDayPosts}
                onSelectPost={handleSelectPost}
              />
            ) : null}
          </div>
        )}
      </div>

      {hoverTooltip && <SchedulePostHoverCardPortal {...hoverTooltip} />}
    </div>
  );
}
